// BusinessVideoConferenceScreen.js
// ─────────────────────────────────────────────────────────────────────────────
// Full-featured business video conference screen with:
//   • Real device camera (expo-camera) — local self-view in lobby AND in-call
//     with graceful Avatar fallback if expo-camera isn't installed
//   • Front/back camera switching, mirror self-view, mute/unmute, speaker toggle
//   • Speaker view vs Grid view toggle
//   • Pin / spotlight a participant — draggable Picture-in-Picture self-view
//   • Network quality indicator + audio level meter (animated mic activity)
//   • Live captions overlay (toggleable, hookable to a real STT service)
//   • Reactions (floating animation), hand-raise queue
//   • Recording with REC badge + duration
//   • In-meeting polls (create / vote / results)
//   • Host controls: Mute all, Lock meeting (prevent new joiners)
//   • Settings modal: quality, captions, mirror self-view, noise suppression
//   • Chat with system messages + emoji picker
//   • Collaborative whiteboard (pen/eraser/rect/circle/text/arrow,
//     colours, undo/redo, clear, export, lock, live cursors via WS)
//   • Meeting notes (auto-saved to server)
//   • Meeting info tab + share invite (link, email)
//   • Time-remaining warning when meeting is about to end
//   • API integration with team_meetings endpoints
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, FlatList,
  Alert, Modal, TextInput, ActivityIndicator, Dimensions,
  KeyboardAvoidingView, Platform, Linking, Share, Vibration,
  PanResponder, Animated, Easing, Pressable,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ── expo-camera with safe fallback ────────────────────────────────────────────
// If expo-camera isn't installed in this build, the screen still works — it
// just shows the Avatar placeholder instead of a real camera feed.
let CameraView = null;
let useCameraPermissions = null;
try {
  // eslint-disable-next-line global-require
  const expoCamera = require('expo-camera');
  CameraView = expoCamera.CameraView || expoCamera.Camera || null;
  useCameraPermissions = expoCamera.useCameraPermissions || null;
} catch (e) {
  // expo-camera not installed — fallback paths handle this case
}
const CAMERA_AVAILABLE = !!CameraView;

const { width, height } = Dimensions.get('window');
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// ── API ──────────────────────────────────────────────────────────────────────
const api = async (path, method = 'GET', body = null) => {
  const token =
    (await AsyncStorage.getItem('auth_token')) ||
    (await AsyncStorage.getItem('userToken')) ||
    (await AsyncStorage.getItem('token'));
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw { status: res.status, ...e }; }
  return res.json();
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDuration = (secs) => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`
    : `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
};
const fmtTime = (d) => new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const initials = (name = '') => name.split(' ').slice(0,2).map(w=>(w[0]||'').toUpperCase()).join('') || '?';

const AVATAR_COLORS = [
  '#0e4e96','#1976D2','#388E3C','#F57C00','#7B1FA2',
  '#D32F2F','#0097A7','#5D4037','#C2185B','#455A64',
];
const colorFor = (s='') => AVATAR_COLORS[s.split('').reduce((a,c)=>a+c.charCodeAt(0),0) % AVATAR_COLORS.length];

const REACTIONS = ['👍','👏','❤️','😄','🎉','🙌','✋','🔥','😮','🤔'];

const NETWORK_LABEL = { 4: 'Excellent', 3: 'Good', 2: 'Fair', 1: 'Poor', 0: 'Offline' };
const NETWORK_COLOR = { 4: '#69F0AE', 3: '#A5D6A7', 2: '#FFD54F', 1: '#FF8A65', 0: '#EF5350' };

// Demo caption fragments — used for the "Live captions" simulator.
// In production this stream comes from a real STT service (e.g. Whisper/Deepgram).
const DEMO_CAPTIONS = [
  'Good morning everyone, thanks for joining',
  'Let me share my screen quickly',
  'Could you mute your mic please',
  'I think we should move this to next week',
  'Any questions on this section',
  'The new figures look much better',
  'Let me take that as an action item',
  'Sorry, can you say that again',
  'We need to finalise this by Friday',
  'Great point, let me note that down',
];

// ── Sub-components ────────────────────────────────────────────────────────────
const Avatar = ({ name, size = 44, color, speaking, muted }) => {
  const bg = color || colorFor(name);
  return (
    <View style={{ position: 'relative' }}>
      <View style={[{
        width: size, height: size, borderRadius: size/2, backgroundColor: bg,
        alignItems: 'center', justifyContent: 'center',
        borderWidth: speaking ? 2.5 : 0, borderColor: '#69F0AE',
      }]}>
        <Text style={{ color: '#fff', fontSize: size * 0.36, fontWeight: '800' }}>{initials(name)}</Text>
      </View>
      {muted && (
        <View style={{
          position: 'absolute', bottom: -2, right: -2,
          width: size * 0.36, height: size * 0.36, borderRadius: size * 0.18,
          backgroundColor: '#D32F2F', alignItems: 'center', justifyContent: 'center',
          borderWidth: 1.5, borderColor: '#fff',
        }}>
          <Ionicons name="mic-off" size={size * 0.2} color="#fff" />
        </View>
      )}
    </View>
  );
};

const ControlBtn = ({ icon, label, active, onPress, danger, size = 22, disabled, badge }) => (
  <TouchableOpacity onPress={onPress} disabled={disabled} style={{ alignItems: 'center', gap: 5 }}
    activeOpacity={0.7}>
    <View style={[{
      width: 50, height: 50, borderRadius: 25, position: 'relative',
      backgroundColor: danger ? '#D32F2F' : active ? '#fff' : 'rgba(255,255,255,0.15)',
      alignItems: 'center', justifyContent: 'center',
    }, disabled && { opacity: 0.4 }]}>
      <Ionicons name={icon} size={size} color={danger ? '#fff' : active ? '#0e4e96' : '#fff'} />
      {badge > 0 && (
        <View style={{
          position: 'absolute', top: -2, right: -2,
          minWidth: 16, height: 16, borderRadius: 8, paddingHorizontal: 4,
          backgroundColor: '#D32F2F', alignItems: 'center', justifyContent: 'center',
          borderWidth: 1.5, borderColor: '#1a1a2e',
        }}>
          <Text style={{ color: '#fff', fontSize: 9, fontWeight: '800' }}>{badge}</Text>
        </View>
      )}
    </View>
    {label && <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 10, fontWeight: '600' }}>{label}</Text>}
  </TouchableOpacity>
);

// Real-camera tile with Avatar fallback
const CameraTile = ({
  facing = 'front',
  enabled,
  mirror = true,
  permissionGranted = false,
  name,
  size = 52,
  speaking,
  muted,
  style,
}) => {
  if (enabled && CAMERA_AVAILABLE && permissionGranted) {
    return (
      <CameraView
        style={[{ flex: 1, width: '100%', height: '100%' }, style,
          mirror && facing === 'front' ? { transform: [{ scaleX: -1 }] } : null]}
        facing={facing}
        mute
      />
    );
  }
  return (
    <View style={[{ flex: 1, alignItems: 'center', justifyContent: 'center' }, style]}>
      <Avatar name={name} size={size} speaking={speaking} muted={muted} />
    </View>
  );
};

// Animated network quality bars (4 bars)
const NetworkBars = ({ quality = 4, size = 12 }) => {
  const color = NETWORK_COLOR[quality] || '#90A4AE';
  return (
    <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 1.5, height: size }}>
      {[1, 2, 3, 4].map(i => (
        <View key={i} style={{
          width: 2.5, height: (size * i) / 4,
          backgroundColor: i <= quality ? color : 'rgba(255,255,255,0.25)',
          borderRadius: 1,
        }} />
      ))}
    </View>
  );
};

// Animated audio level meter — uses a shared Animated.Value
const AudioBars = ({ levelAnim, color = '#69F0AE', height: barH = 18 }) => {
  const bar = (i) => {
    const range = levelAnim.interpolate({
      inputRange: [0, 1],
      outputRange: [barH * 0.2, barH * (0.45 + (i % 3) * 0.2)],
    });
    return <Animated.View key={i} style={{ width: 2.5, marginHorizontal: 1, borderRadius: 1, backgroundColor: color, height: range }} />;
  };
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', height: barH }}>
      {[0,1,2,3,4].map(bar)}
    </View>
  );
};

// ── MAIN ──────────────────────────────────────────────────────────────────────
export default function BusinessVideoConferenceScreen({ navigation, route }) {
  const {
    meetingId,
    meetingTitle,
    participantName,
    autoJoin = false,
  } = route?.params || {};

  // ── User context ─────────────────────────────────────────────────────────────
  const [companyId, setCompanyId] = useState(null);
  const [userId,    setUserId]    = useState(null);
  const [userName,  setUserName]  = useState('Me');

  // ── Meeting data ─────────────────────────────────────────────────────────────
  const [meeting,      setMeeting]      = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading,      setLoading]      = useState(true);

  // ── Call state ───────────────────────────────────────────────────────────────
  const [inCall,      setInCall]      = useState(false);
  const [callPhase,   setCallPhase]   = useState('lobby'); // lobby | active | ended
  const [duration,    setDuration]    = useState(0);
  const [joining,     setJoining]     = useState(false);
  const [leaving,     setLeaving]     = useState(false);
  const [meetingLocked, setMeetingLocked] = useState(false);
  const [timeWarningShown, setTimeWarningShown] = useState(false);

  // ── Media controls ───────────────────────────────────────────────────────────
  const [micOn,        setMicOn]        = useState(true);
  const [cameraOn,     setCameraOn]     = useState(false);
  const [speakerOn,    setSpeakerOn]    = useState(true);
  const [handRaised,   setHandRaised]   = useState(false);
  const [screenShare,  setScreenShare]  = useState(false);
  const [recording,    setRecording]    = useState(false);
  const [recordingTime,setRecordingTime]= useState(0);
  const [cameraFacing, setCameraFacing] = useState('front'); // 'front' | 'back'

  // expo-camera permission (only used if CAMERA_AVAILABLE). useFallback is a no-op
  // hook so the hook call itself stays unconditional, even if the module is missing.
  const useFallbackPermission = () => [
    { granted: false, canAskAgain: true, status: 'undetermined' },
    async () => ({ granted: false }),
  ];
  const usePerm = useCameraPermissions || useFallbackPermission;
  const [permission, requestPermission] = usePerm();

  // ── View / layout state ──────────────────────────────────────────────────────
  const [viewMode,      setViewMode]      = useState('grid');     // grid | speaker
  const [pinnedId,      setPinnedId]      = useState(null);       // participant id or 'me'
  const [activeTab,     setActiveTab]     = useState('participants'); // participants | chat | whiteboard | info | notes | polls
  const [showControls,  setShowControls]  = useState(true);
  const [controlsTimer, setControlsTimer] = useState(null);
  const [mirrorSelf,    setMirrorSelf]    = useState(true);

  // ── Modals ───────────────────────────────────────────────────────────────────
  const [showEndModal,    setShowEndModal]    = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showSettings,    setShowSettings]    = useState(false);
  const [showMoreMenu,    setShowMoreMenu]    = useState(false);
  const [showPollModal,   setShowPollModal]   = useState(false);
  const [showCreatePoll,  setShowCreatePoll]  = useState(false);

  // ── Chat ─────────────────────────────────────────────────────────────────────
  const [messages,    setMessages]    = useState([]);
  const [chatInput,   setChatInput]   = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [showReactions, setShowReactions] = useState(false);

  // ── Notes ────────────────────────────────────────────────────────────────────
  const [notes,      setNotes]      = useState('');
  const [savedNotes, setSavedNotes] = useState('');

  // ── Floating reactions ───────────────────────────────────────────────────────
  const [reactions, setReactions] = useState([]);

  // ── Live captions ────────────────────────────────────────────────────────────
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const [captions,        setCaptions]        = useState([]); // last few
  const captionTimer = useRef(null);

  // ── Polls ────────────────────────────────────────────────────────────────────
  const [polls,        setPolls]        = useState([]); // {id, question, options:[{text,votes}], voted, createdBy, closed}
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions,  setPollOptions]  = useState(['', '']);

  // ── Network & audio ──────────────────────────────────────────────────────────
  const [networkQuality, setNetworkQuality] = useState(4);
  const audioLevelAnim = useRef(new Animated.Value(0.2)).current;

  // ── Settings ─────────────────────────────────────────────────────────────────
  const [videoQuality,     setVideoQuality]     = useState('auto'); // auto | hd | sd
  const [noiseSuppression, setNoiseSuppression] = useState(true);
  const [captionLang,      setCaptionLang]      = useState('en');

  // ── Whiteboard (unchanged from original) ─────────────────────────────────────
  const [wbElements,    setWbElements]    = useState([]);
  const [wbTool,        setWbTool]        = useState('pen');
  const [wbColor,       setWbColor]       = useState('#263238');
  const [wbStroke,      setWbStroke]      = useState(3);
  const [wbFill,        setWbFill]        = useState('none');
  const [wbDrawing,     setWbDrawing]     = useState(false);
  const [wbPath,        setWbPath]        = useState([]);
  const [wbId,          setWbId]          = useState(null);
  const [wbLoading,     setWbLoading]     = useState(false);
  const [wbLocked,      setWbLocked]      = useState(false);
  const [wbHistory,     setWbHistory]     = useState([]);
  const [wbRedo,        setWbRedo]        = useState([]);
  const [wbTextInput,   setWbTextInput]   = useState('');
  const [wbTextModal,   setWbTextModal]   = useState(false);
  const [wbTextPos,     setWbTextPos]     = useState({ x: 0, y: 0 });
  const [wbActiveUsers, setWbActiveUsers] = useState([]);
  const [wbSelected,    setWbSelected]    = useState(null);
  const wsRef = useRef(null);
  const elementIdCounter = useRef(1);

  // ── Speaking simulation (will be replaced by audio activity from WebRTC) ─────
  const [speaking, setSpeaking] = useState(null);

  // ── PiP (self-view) drag ─────────────────────────────────────────────────────
  const pipPos = useRef(new Animated.ValueXY({ x: width - 130, y: 20 })).current;
  const pipPanResponder = useMemo(() => PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onPanResponderGrant: () => {
      pipPos.extractOffset();
    },
    onPanResponderMove: Animated.event([null, { dx: pipPos.x, dy: pipPos.y }], { useNativeDriver: false }),
    onPanResponderRelease: () => {
      pipPos.flattenOffset();
    },
  }), [pipPos]);

  // ── Timers ───────────────────────────────────────────────────────────────────
  const durationTimer = useRef(null);
  const recordTimer   = useRef(null);
  const chatRef       = useRef(null);
  const networkTimer  = useRef(null);

  // ── Bootstrap ────────────────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      AsyncStorage.getItem('company_id'),
      AsyncStorage.getItem('user_id'),
      AsyncStorage.getItem('user_name'),
    ]).then(([cid, uid, name]) => {
      if (cid)  setCompanyId(cid);
      if (uid)  setUserId(uid);
      if (name) setUserName(name);
    });
  }, []);

  useEffect(() => {
    if (companyId) {
      if (meetingId) fetchMeeting();
      else synthesisMeeting();
    }
  }, [companyId, meetingId]);

  useEffect(() => {
    if (autoJoin && !inCall && meeting) joinCall();
  }, [meeting, autoJoin]);

  // Ensure camera permission is requested up-front (so toggling cam in-call is instant)
  useEffect(() => {
    if (CAMERA_AVAILABLE && permission && !permission.granted && permission.canAskAgain) {
      // Don't auto-request — wait for user to toggle camera
    }
  }, [permission]);

  // ── Fetch meeting details ─────────────────────────────────────────────────────
  const fetchMeeting = async () => {
    setLoading(true);
    try {
      const r = await api(`/api/data/team_meetings/${meetingId}`);
      const m = r.data ?? r.meeting ?? r;
      setMeeting(m);
      const memberIds = m.participants || [];
      if (memberIds.length > 0) {
        const rp = await api(`/api/data/team_members?company_id=${companyId}`);
        const all = Array.isArray(rp) ? rp : (rp.data ?? rp.team_members ?? []);
        const pax = all.filter(tm => memberIds.includes(tm.id));
        setParticipants(pax.map(p => ({
          ...p, muted: false, cameraOff: true, handRaised: false,
          joined: false, joinedAt: null, networkQuality: 3 + Math.floor(Math.random()),
        })));
      }
      if (m.notes) setNotes(m.notes);
      setSavedNotes(m.notes || '');
    } catch {
      synthesisMeeting();
    } finally {
      setLoading(false);
    }
  };

  const synthesisMeeting = () => {
    const now = new Date();
    setMeeting({
      id: null,
      title: meetingTitle || 'Instant Meeting',
      meeting_date: now.toISOString().split('T')[0],
      time: `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`,
      duration_minutes: 60,
      type: 'one-time',
      location: '',
      agenda: '',
      notes: '',
    });
    if (participantName) {
      setParticipants([{
        id: 'guest-1', name: participantName,
        role: 'Guest', department: '',
        muted: false, cameraOff: true, handRaised: false,
        joined: false, joinedAt: null, networkQuality: 4,
      }]);
    }
    setLoading(false);
  };

  // ── Camera permission flow ───────────────────────────────────────────────────
  const ensureCameraPermission = async () => {
    if (!CAMERA_AVAILABLE) {
      Alert.alert(
        'Camera Module Missing',
        'Install expo-camera with `npx expo install expo-camera` to enable real video.',
        [{ text: 'OK' }],
      );
      return false;
    }
    if (permission?.granted) return true;
    if (!permission?.canAskAgain) {
      Alert.alert(
        'Camera Permission Needed',
        'Please enable camera access for JobEng in your device settings.',
        [{ text: 'Open Settings', onPress: () => Linking.openSettings() }, { text: 'Cancel', style: 'cancel' }],
      );
      return false;
    }
    const result = await requestPermission();
    return !!result?.granted;
  };

  const toggleCamera = async () => {
    if (cameraOn) { setCameraOn(false); return; }
    const ok = await ensureCameraPermission();
    if (ok) setCameraOn(true);
  };

  const flipCamera = () => {
    setCameraFacing(f => f === 'front' ? 'back' : 'front');
    Vibration.vibrate(20);
  };

  // ── Call lifecycle ────────────────────────────────────────────────────────────
  const joinCall = async () => {
    setJoining(true);
    Vibration.vibrate(50);
    try {
      if (meetingId) {
        await api(`/api/data/team_meetings/${meetingId}`, 'PUT', {
          company_id: companyId,
          status: 'active',
          started_at: new Date().toISOString(),
        }).catch(() => {});
      }
      setParticipants(prev => prev.map((p, i) => ({
        ...p,
        joined: i < 2,
        joinedAt: i < 2 ? new Date() : null,
      })));
      setInCall(true);
      setCallPhase('active');
      addSystemMessage(`${userName} joined the meeting`);
      initWhiteboard();
      durationTimer.current = setInterval(() => setDuration(d => d + 1), 1000);
      startSpeakingSimulation();
      startNetworkSimulation();
      startAudioLevelAnimation();
    } finally {
      setJoining(false);
    }
  };

  const leaveCall = async (endForAll = false) => {
    setLeaving(true);
    try {
      stopTimers();
      if (endForAll && meetingId) {
        await api(`/api/data/team_meetings/${meetingId}`, 'PUT', {
          company_id: companyId,
          status: 'completed',
          ended_at: new Date().toISOString(),
          duration_actual: duration,
        }).catch(() => {});
      }
      if (recording) await stopRecording();
      if (notes !== savedNotes) await saveNotes();
      setInCall(false);
      setCallPhase('ended');
      setShowEndModal(false);
      setCameraOn(false);
    } finally {
      setLeaving(false);
    }
  };

  const stopTimers = () => {
    if (durationTimer.current) { clearInterval(durationTimer.current); durationTimer.current = null; }
    if (recordTimer.current)   { clearInterval(recordTimer.current);   recordTimer.current   = null; }
    if (networkTimer.current)  { clearInterval(networkTimer.current);  networkTimer.current  = null; }
    if (captionTimer.current)  { clearInterval(captionTimer.current);  captionTimer.current  = null; }
    if (controlsTimer)         { clearTimeout(controlsTimer); }
    audioLevelAnim.stopAnimation();
  };

  useEffect(() => () => stopTimers(), []); // eslint-disable-line

  // ── Time-remaining warning ───────────────────────────────────────────────────
  useEffect(() => {
    if (!inCall || !meeting?.duration_minutes || timeWarningShown) return;
    const totalSec = meeting.duration_minutes * 60;
    if (duration >= totalSec - 300 && duration < totalSec - 270) {
      setTimeWarningShown(true);
      addSystemMessage('⏰ Meeting ends in about 5 minutes');
      Vibration.vibrate([0, 100, 100, 100]);
    }
  }, [duration, inCall, meeting, timeWarningShown]);

  // ── Speaking + network + audio simulation ────────────────────────────────────
  const startSpeakingSimulation = () => {
    const paxWithMe = [
      { id: 'me', name: userName },
      ...participants.slice(0, 2),
    ];
    if (paxWithMe.length < 2) return;
    let idx = 0;
    const interval = setInterval(() => {
      idx = (idx + 1) % paxWithMe.length;
      setSpeaking(paxWithMe[idx]?.id || null);
    }, 3500);
    // store on a ref-equivalent — clears when component unmounts via stopTimers
    networkTimer.currentSpeaking = interval;
  };

  const startNetworkSimulation = () => {
    networkTimer.current = setInterval(() => {
      // Gentle drift between 3 and 4, occasional dip to 2
      const r = Math.random();
      setNetworkQuality(r < 0.05 ? 2 : r < 0.3 ? 3 : 4);
    }, 8000);
  };

  const startAudioLevelAnimation = () => {
    const loop = () => {
      Animated.sequence([
        Animated.timing(audioLevelAnim, {
          toValue: micOn ? 0.6 + Math.random() * 0.4 : 0.1,
          duration: 180,
          easing: Easing.out(Easing.quad),
          useNativeDriver: false,
        }),
        Animated.timing(audioLevelAnim, {
          toValue: micOn ? 0.2 + Math.random() * 0.3 : 0.1,
          duration: 220,
          easing: Easing.in(Easing.quad),
          useNativeDriver: false,
        }),
      ]).start(({ finished }) => { if (finished) loop(); });
    };
    loop();
  };

  // ── Captions ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (captionsEnabled && inCall) {
      // Demo: rotate through demo captions every ~4s. Replace with real STT stream.
      let i = 0;
      captionTimer.current = setInterval(() => {
        const speakers = [
          userName,
          ...participants.filter(p => p.joined).map(p => p.name),
        ];
        const speaker = speakers[i % speakers.length] || userName;
        const text = DEMO_CAPTIONS[i % DEMO_CAPTIONS.length];
        const caption = { id: Date.now() + i, speaker, text, time: new Date() };
        setCaptions(prev => [...prev.slice(-3), caption]);
        i++;
      }, 4000);
    } else if (captionTimer.current) {
      clearInterval(captionTimer.current);
      captionTimer.current = null;
      setCaptions([]);
    }
    return () => {
      if (captionTimer.current) { clearInterval(captionTimer.current); captionTimer.current = null; }
    };
  }, [captionsEnabled, inCall, participants, userName]);

  // ── Recording ─────────────────────────────────────────────────────────────────
  const toggleRecording = async () => {
    if (!recording) {
      Alert.alert(
        'Start Recording',
        'All participants will be notified that this call is being recorded.',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Record', onPress: async () => {
            setRecording(true);
            setRecordingTime(0);
            recordTimer.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
            addSystemMessage('Recording started');
            if (meetingId) {
              api(`/api/data/team_meetings/${meetingId}`, 'PUT', {
                company_id: companyId, is_recorded: true,
                recording_started_at: new Date().toISOString(),
              }).catch(() => {});
            }
          }},
        ],
      );
    } else {
      await stopRecording();
    }
  };

  const stopRecording = async () => {
    if (recordTimer.current) { clearInterval(recordTimer.current); recordTimer.current = null; }
    setRecording(false);
    addSystemMessage(`Recording saved (${fmtDuration(recordingTime)})`);
    if (meetingId) {
      api(`/api/data/team_meetings/${meetingId}`, 'PUT', {
        company_id: companyId,
        recording_duration: recordingTime,
        recording_ended_at: new Date().toISOString(),
      }).catch(() => {});
    }
  };

  // ── Chat ──────────────────────────────────────────────────────────────────────
  const sendMessage = () => {
    const text = chatInput.trim();
    if (!text) return;
    const msg = {
      id: Date.now(), sender: userName, senderId: userId || 'me',
      text, time: new Date(), type: 'chat',
    };
    setMessages(prev => [...prev, msg]);
    setChatInput('');
    if (activeTab !== 'chat') setUnreadCount(c => c + 1);
    setTimeout(() => chatRef.current?.scrollToEnd({ animated: true }), 100);
    if (meetingId) {
      api(`/api/data/team_meetings/${meetingId}`, 'PUT', {
        company_id: companyId,
        chat_log: JSON.stringify([...messages, msg].map(m => ({
          sender: m.sender, text: m.text, time: m.time,
        }))),
      }).catch(() => {});
    }
  };

  const addSystemMessage = (text) => {
    setMessages(prev => [...prev, {
      id: Date.now() + Math.random(), sender: 'System', text, time: new Date(), type: 'system',
    }]);
  };

  // ── Reactions ────────────────────────────────────────────────────────────────
  const sendReaction = (emoji) => {
    const r = { id: Date.now() + Math.random(), emoji, name: userName,
      x: 20 + Math.random() * (width - 80),
      y: 180 + Math.random() * 80,
    };
    setReactions(prev => [...prev, r]);
    setTimeout(() => setReactions(prev => prev.filter(x => x.id !== r.id)), 3000);
    setShowReactions(false);
    addSystemMessage(`${userName} reacted ${emoji}`);
  };

  // ── Notes ────────────────────────────────────────────────────────────────────
  const saveNotes = async () => {
    setSavedNotes(notes);
    if (meetingId) {
      await api(`/api/data/team_meetings/${meetingId}`, 'PUT', {
        company_id: companyId, notes,
      }).catch(() => {});
    }
  };

  // ── Polls ────────────────────────────────────────────────────────────────────
  const openCreatePoll = () => {
    setPollQuestion('');
    setPollOptions(['', '']);
    setShowCreatePoll(true);
  };

  const createPoll = () => {
    const q = pollQuestion.trim();
    const opts = pollOptions.map(o => o.trim()).filter(Boolean);
    if (!q || opts.length < 2) {
      Alert.alert('Incomplete', 'Add a question and at least 2 options.');
      return;
    }
    const poll = {
      id: Date.now(),
      question: q,
      options: opts.map(text => ({ text, votes: 0 })),
      voted: false,
      createdBy: userName,
      closed: false,
      createdAt: new Date(),
    };
    setPolls(prev => [...prev, poll]);
    setShowCreatePoll(false);
    setShowPollModal(false);
    addSystemMessage(`${userName} created a poll: "${q}"`);
    Vibration.vibrate(30);
  };

  const votePoll = (pollId, optIdx) => {
    setPolls(prev => prev.map(p => {
      if (p.id !== pollId || p.voted || p.closed) return p;
      const opts = p.options.map((o, i) => i === optIdx ? { ...o, votes: o.votes + 1 } : o);
      return { ...p, options: opts, voted: true };
    }));
  };

  const closePoll = (pollId) => {
    setPolls(prev => prev.map(p => p.id === pollId ? { ...p, closed: true } : p));
    addSystemMessage('Poll closed');
  };

  // ── Host actions ─────────────────────────────────────────────────────────────
  const muteAll = () => {
    Alert.alert('Mute Everyone', 'Mute all participants? They can unmute themselves.', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Mute All', onPress: () => {
        setParticipants(prev => prev.map(p => ({ ...p, muted: true })));
        addSystemMessage('Host muted everyone');
        setShowMoreMenu(false);
      }},
    ]);
  };

  const toggleMeetingLock = () => {
    setMeetingLocked(v => {
      const next = !v;
      addSystemMessage(next ? 'Meeting locked — no new participants' : 'Meeting unlocked');
      setShowMoreMenu(false);
      return next;
    });
  };

  const togglePin = (id) => {
    setPinnedId(prev => prev === id ? null : id);
    setViewMode(prev => prev === 'grid' ? 'speaker' : prev);
    Vibration.vibrate(20);
  };

  // ── Whiteboard (unchanged) ───────────────────────────────────────────────────
  const initWhiteboard = async () => {
    if (!meetingId) return;
    setWbLoading(true);
    try {
      let wb;
      try {
        const r = await api(`/api/meetings/${meetingId}/whiteboard`);
        wb = r.data ?? r;
      } catch {
        const r = await api(`/api/meetings/${meetingId}/whiteboard`, 'POST', {
          meeting_id: meetingId, title: meeting?.title || 'Meeting Board',
        });
        wb = r.data ?? r;
      }
      if (wb?.id) {
        setWbId(wb.id);
        await fetchWbElements(wb.id);
        connectWbWebSocket(wb.id);
      }
    } catch (e) {
      console.warn('Whiteboard init error:', e?.message);
    } finally { setWbLoading(false); }
  };

  const fetchWbElements = async (id) => {
    try {
      const r = await api(`/api/whiteboard/${id}/elements`);
      const els = Array.isArray(r) ? r : (r.data ?? r.elements ?? []);
      setWbElements(els);
    } catch { setWbElements([]); }
  };

  const connectWbWebSocket = (id) => {
    try {
      if (wsRef.current) wsRef.current.close();
      const ws = new WebSocket(`wss://api.jobeng.ai/whiteboard/${id}/live`);
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          switch (msg.type) {
            case 'element_added':
              setWbElements(prev => [...prev.filter(e => e.id !== msg.element.id), msg.element]);
              break;
            case 'element_updated':
              setWbElements(prev => prev.map(e => e.id === msg.element.id ? msg.element : e));
              break;
            case 'element_deleted':
              setWbElements(prev => prev.filter(e => e.id !== msg.elementId));
              break;
            case 'cleared':
              setWbElements([]);
              break;
            case 'user_joined':
              setWbActiveUsers(prev => [...prev.filter(u => u.id !== msg.user.id), msg.user]);
              break;
            case 'user_left':
              setWbActiveUsers(prev => prev.filter(u => u.id !== msg.userId));
              break;
          }
        } catch {}
      };
      ws.onclose = () => wsRef.current = null;
      wsRef.current = ws;
    } catch (e) { console.warn('WS connect failed:', e?.message); }
  };

  const wbAddElement = async (elementData) => {
    if (wbLocked) return;
    const localId = `local_${elementIdCounter.current++}`;
    const el = { id: localId, created_by: userName, created_at: new Date().toISOString(), ...elementData };
    setWbElements(prev => [...prev, el]);
    setWbHistory(prev => [...prev, { action: 'add', id: localId }]);
    setWbRedo([]);
    if (wbId) {
      try {
        const r = await api(`/api/whiteboard/${wbId}/elements`, 'POST', {
          ...elementData, created_by: userName,
        });
        const saved = r.data ?? r;
        if (saved?.id) {
          setWbElements(prev => prev.map(e => e.id === localId ? { ...saved } : e));
        }
      } catch {}
    }
  };

  const wbDeleteElement = async (elementId) => {
    setWbElements(prev => prev.filter(e => e.id !== elementId));
    setWbSelected(null);
    if (wbId) {
      api(`/api/whiteboard/elements/${elementId}`, 'DELETE').catch(() => {});
    }
  };

  const wbClear = () => {
    Alert.alert('Clear Board', 'Remove all elements from the whiteboard?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Clear', style: 'destructive', onPress: async () => {
        setWbElements([]);
        setWbHistory([]);
        setWbRedo([]);
        if (wbId) api(`/api/whiteboard/${wbId}/clear`, 'POST').catch(() => {});
      }},
    ]);
  };

  const wbUndo = () => {
    if (!wbHistory.length) return;
    const last = wbHistory[wbHistory.length - 1];
    setWbRedo(prev => [...prev, last]);
    setWbHistory(prev => prev.slice(0, -1));
    if (last.action === 'add') {
      setWbElements(prev => prev.filter(e => e.id !== last.id));
      if (wbId) api(`/api/whiteboard/${wbId}/undo`, 'POST').catch(() => {});
    }
  };

  const wbRedoAction = () => {
    if (!wbRedo.length) return;
    const next = wbRedo[wbRedo.length - 1];
    setWbHistory(prev => [...prev, next]);
    setWbRedo(prev => prev.slice(0, -1));
    if (wbId) api(`/api/whiteboard/${wbId}/redo`, 'POST').catch(() => {});
  };

  const wbExport = async () => {
    if (!wbId) { Alert.alert('Export', 'No whiteboard to export'); return; }
    try {
      const r = await api(`/api/whiteboard/${wbId}/export`, 'POST', { format: 'png' });
      const url = r.data?.url ?? r.url;
      if (url) Linking.openURL(url).catch(() => {});
      else Alert.alert('Export', 'Export started. Check your downloads.');
    } catch { Alert.alert('Export Failed', 'Could not export whiteboard.'); }
  };

  const pointsToPath = (pts) => {
    if (!pts.length) return '';
    return pts.reduce((acc, p, i) => acc + (i === 0 ? `M ${p.x} ${p.y}` : ` L ${p.x} ${p.y}`), '');
  };

  const finishDraw = async () => {
    if (!wbPath.length) { setWbDrawing(false); setWbPath([]); return; }
    if (wbTool === 'pen') {
      await wbAddElement({
        type: 'path', x: 0, y: 0,
        data: { path: pointsToPath(wbPath) },
        style: { stroke: wbColor, strokeWidth: wbStroke, opacity: 1 },
        created_by: userName,
      });
    } else if (wbTool === 'eraser') {
      const eraseRadius = 20;
      setWbElements(prev => prev.filter(el => {
        if (el.type !== 'path') return true;
        return !wbPath.some(p => {
          const pts = (el.data?.path || '').split(' ').filter(s => s !== 'M' && s !== 'L');
          return pts.some((_, i) => {
            if (i % 2 !== 0) return false;
            const ex = parseFloat(pts[i]), ey = parseFloat(pts[i+1]);
            return Math.hypot(ex - p.x, ey - p.y) < eraseRadius;
          });
        });
      }));
    }
    setWbDrawing(false);
    setWbPath([]);
  };

  const addTextElement = async () => {
    if (!wbTextInput.trim()) { setWbTextModal(false); return; }
    await wbAddElement({
      type: 'text', x: wbTextPos.x, y: wbTextPos.y,
      text: wbTextInput.trim(),
      style: { stroke: wbColor, fontSize: 18, fontFamily: 'Arial', opacity: 1 },
      created_by: userName,
    });
    setWbTextModal(false);
    setWbTextInput('');
  };

  const addShapeAtCenter = async (type) => {
    const cx = 960, cy = 540;
    if (type === 'rect') {
      await wbAddElement({
        type: 'rectangle', x: cx - 80, y: cy - 50,
        data: { width: 160, height: 100 },
        style: { stroke: wbColor, strokeWidth: wbStroke, fill: wbFill === 'none' ? 'transparent' : wbFill, opacity: 1 },
        created_by: userName,
      });
    } else if (type === 'circle') {
      await wbAddElement({
        type: 'circle', x: cx, y: cy,
        data: { radius: 60 },
        style: { stroke: wbColor, strokeWidth: wbStroke, fill: wbFill === 'none' ? 'transparent' : wbFill, opacity: 1 },
        created_by: userName,
      });
    } else if (type === 'arrow') {
      await wbAddElement({
        type: 'arrow', x: cx - 80, y: cy,
        data: { x2: cx + 80, y2: cy },
        style: { stroke: wbColor, strokeWidth: wbStroke, opacity: 1 },
        created_by: userName,
      });
    }
  };

  // ── Controls auto-hide ────────────────────────────────────────────────────────
  const touchScreen = () => {
    setShowControls(true);
    if (controlsTimer) clearTimeout(controlsTimer);
    const t = setTimeout(() => setShowControls(false), 5000);
    setControlsTimer(t);
  };

  // ── Participant actions ───────────────────────────────────────────────────────
  const muteParticipant = (p) => {
    Alert.alert('Mute Participant', `Mute ${p.name}?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Mute', onPress: () => {
        setParticipants(prev => prev.map(x => x.id === p.id ? { ...x, muted: true } : x));
        addSystemMessage(`${p.name} was muted by host`);
      }},
    ]);
  };

  const removeParticipant = (p) => {
    Alert.alert('Remove', `Remove ${p.name} from the call?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Remove', style: 'destructive', onPress: () => {
        setParticipants(prev => prev.filter(x => x.id !== p.id));
        addSystemMessage(`${p.name} was removed from the meeting`);
      }},
    ]);
  };

  // ── Invite ───────────────────────────────────────────────────────────────────
  const copyMeetingLink = () => {
    if (meetingLocked) {
      Alert.alert('Meeting Locked', 'Unlock the meeting before inviting new participants.');
      return;
    }
    const link = meeting?.location || `https://meet.jobeng.ai/${meetingId || 'instant'}`;
    Share.share({ message: `Join meeting: ${meeting?.title}\n${link}`, title: 'Meeting Invite' });
  };

  // ── Tab switching ─────────────────────────────────────────────────────────────
  const switchTab = (tab) => {
    setActiveTab(tab);
    if (tab === 'chat') setUnreadCount(0);
  };

  // ── Derived ──────────────────────────────────────────────────────────────────
  const joinedParticipants = participants.filter(p => p.joined);
  const waitingParticipants = participants.filter(p => !p.joined);
  const totalInCall = joinedParticipants.length + (inCall ? 1 : 0);
  const handRaiseQueue = joinedParticipants.filter(p => p.handRaised);
  const activePoll = polls.find(p => !p.closed);
  const pinnedPerson = pinnedId === 'me'
    ? { id: 'me', name: userName, muted: !micOn, cameraOff: !cameraOn, isMe: true }
    : joinedParticipants.find(p => p.id === pinnedId) || null;

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER HELPERS — Tab content (kept from original, lightly enhanced)
  // ════════════════════════════════════════════════════════════════════════════

  // ── Tab: Participants ─────────────────────────────────────────────────────────
  const renderParticipants = () => (
    <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}
      contentContainerStyle={{ paddingBottom: 20 }}>
      {handRaiseQueue.length > 0 && (
        <View style={St.handQueueBar}>
          <Text style={{ fontSize: 11, color: '#F57C00', fontWeight: '800' }}>✋ HAND RAISED ({handRaiseQueue.length})</Text>
          <Text style={{ fontSize: 12, color: '#78909C', marginTop: 2 }} numberOfLines={1}>
            {handRaiseQueue.map(p => p.name.split(' ')[0]).join(', ')}
          </Text>
        </View>
      )}

      {inCall && (
        <TouchableOpacity style={St.paxCard} onLongPress={() => togglePin('me')}>
          <Avatar name={userName} size={44} speaking={speaking === 'me'} muted={!micOn} />
          <View style={{ flex: 1, marginLeft: 12 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Text style={St.paxName}>{userName}</Text>
              <View style={St.youBadge}><Text style={{ fontSize: 10, color: '#0e4e96', fontWeight: '800' }}>YOU</Text></View>
              {handRaised && <Text>✋</Text>}
              {pinnedId === 'me' && <Ionicons name="pin" size={14} color="#F57C00" />}
            </View>
            <Text style={St.paxRole}>Host</Text>
          </View>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <View style={[St.paxIcon, { backgroundColor: micOn ? '#E8F5E9' : '#FFEBEE' }]}>
              <Ionicons name={micOn ? 'mic' : 'mic-off'} size={16} color={micOn ? '#388E3C' : '#D32F2F'} />
            </View>
            <View style={[St.paxIcon, { backgroundColor: cameraOn ? '#E3F2FD' : '#F5F5F5' }]}>
              <Ionicons name={cameraOn ? 'videocam' : 'videocam-off'} size={16}
                color={cameraOn ? '#1976D2' : '#9E9E9E'} />
            </View>
          </View>
        </TouchableOpacity>
      )}

      {joinedParticipants.length > 0 && (
        <View>
          <Text style={St.paxSection}>In Call ({joinedParticipants.length})</Text>
          {joinedParticipants.map((p, i) => (
            <TouchableOpacity key={p.id || i} style={St.paxCard}
              onPress={() => togglePin(p.id)}
              onLongPress={() => Alert.alert(p.name, 'Participant actions', [
                { text: pinnedId === p.id ? 'Unpin' : 'Pin', onPress: () => togglePin(p.id) },
                { text: 'Mute',   onPress: () => muteParticipant(p) },
                { text: 'Remove', style: 'destructive', onPress: () => removeParticipant(p) },
                { text: 'Cancel', style: 'cancel' },
              ])}>
              <Avatar name={p.name} size={44} color={colorFor(p.name)}
                speaking={speaking === p.id} muted={p.muted} />
              <View style={{ flex: 1, marginLeft: 12 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <Text style={St.paxName}>{p.name}</Text>
                  {p.handRaised && <Text>✋</Text>}
                  {pinnedId === p.id && <Ionicons name="pin" size={14} color="#F57C00" />}
                </View>
                <Text style={St.paxRole}>{p.role || p.department || '—'}</Text>
                {p.joinedAt && (
                  <Text style={{ fontSize: 11, color: '#90A4AE' }}>
                    Joined {fmtTime(p.joinedAt)}
                  </Text>
                )}
              </View>
              <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                <NetworkBars quality={p.networkQuality || 3} size={10} />
                <View style={[St.paxIcon, { backgroundColor: p.muted ? '#FFEBEE' : '#E8F5E9' }]}>
                  <Ionicons name={p.muted ? 'mic-off' : 'mic'} size={16} color={p.muted ? '#D32F2F' : '#388E3C'} />
                </View>
              </View>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {waitingParticipants.length > 0 && (
        <View style={{ marginTop: 10 }}>
          <Text style={St.paxSection}>Invited ({waitingParticipants.length})</Text>
          {waitingParticipants.map((p, i) => (
            <View key={p.id || i} style={[St.paxCard, { opacity: 0.6 }]}>
              <Avatar name={p.name} size={44} color={colorFor(p.name)} />
              <View style={{ flex: 1, marginLeft: 12 }}>
                <Text style={St.paxName}>{p.name}</Text>
                <Text style={St.paxRole}>{p.role || '—'}</Text>
              </View>
              <View style={St.paxIcon}>
                <Ionicons name="time-outline" size={16} color="#9E9E9E" />
              </View>
            </View>
          ))}
        </View>
      )}

      <TouchableOpacity style={St.inviteBtn} onPress={() => setShowInviteModal(true)}>
        <Ionicons name="person-add-outline" size={20} color="#0e4e96" />
        <Text style={{ fontSize: 14, color: '#0e4e96', fontWeight: '700', marginLeft: 8 }}>
          Invite Participant
        </Text>
      </TouchableOpacity>
    </ScrollView>
  );

  // ── Tab: Chat ─────────────────────────────────────────────────────────────────
  const renderChat = () => (
    <KeyboardAvoidingView style={{ flex: 1 }}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={120}>
      <FlatList
        ref={chatRef}
        data={messages}
        keyExtractor={m => String(m.id)}
        contentContainerStyle={{ padding: 14, paddingBottom: 10 }}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={
          <View style={{ alignItems: 'center', paddingTop: 40 }}>
            <Ionicons name="chatbubbles-outline" size={48} color="#CFD8DC" />
            <Text style={{ color: '#B0BEC5', fontSize: 14, marginTop: 10 }}>
              No messages yet
            </Text>
          </View>
        }
        renderItem={({ item: m }) => {
          if (m.type === 'system') return (
            <View style={{ alignItems: 'center', marginVertical: 6 }}>
              <View style={{ backgroundColor: '#F5F5F5', borderRadius: 12, paddingHorizontal: 12, paddingVertical: 4 }}>
                <Text style={{ fontSize: 11, color: '#9E9E9E' }}>{m.text}</Text>
              </View>
            </View>
          );
          const isMe = m.senderId === (userId || 'me');
          return (
            <View style={{ alignItems: isMe ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
              {!isMe && <Text style={{ fontSize: 11, color: '#78909C', marginBottom: 3, marginLeft: 4 }}>{m.sender}</Text>}
              <View style={[St.bubble, isMe ? St.bubbleMe : St.bubbleThem]}>
                <Text style={{ fontSize: 14, color: isMe ? '#fff' : '#263238', lineHeight: 20 }}>{m.text}</Text>
                <Text style={{ fontSize: 10, color: isMe ? 'rgba(255,255,255,0.65)' : '#90A4AE', marginTop: 4, alignSelf: 'flex-end' }}>
                  {fmtTime(m.time)}
                </Text>
              </View>
            </View>
          );
        }}
      />
      <View style={St.chatInputRow}>
        <TextInput
          style={St.chatInput}
          value={chatInput}
          onChangeText={setChatInput}
          placeholder="Type a message…"
          placeholderTextColor="#B0BEC5"
          multiline
          maxLength={500}
          returnKeyType="send"
          onSubmitEditing={sendMessage}
        />
        <TouchableOpacity onPress={() => setShowReactions(v => !v)} style={St.emojiBtn}>
          <Text style={{ fontSize: 20 }}>😊</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={sendMessage} disabled={!chatInput.trim()}
          style={[St.sendBtn, !chatInput.trim() && { opacity: 0.4 }]}>
          <Ionicons name="send" size={20} color="#fff" />
        </TouchableOpacity>
      </View>
      {showReactions && (
        <View style={St.reactionPicker}>
          {REACTIONS.map(e => (
            <TouchableOpacity key={e} onPress={() => sendReaction(e)} style={{ padding: 8 }}>
              <Text style={{ fontSize: 24 }}>{e}</Text>
            </TouchableOpacity>
          ))}
        </View>
      )}
    </KeyboardAvoidingView>
  );

  // ── Tab: Meeting Info ─────────────────────────────────────────────────────────
  const renderInfo = () => (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16 }}>
      <View style={St.infoCard}>
        <Text style={St.infoTitle}>{meeting?.title}</Text>
        {[
          { icon: 'calendar-outline',  label: 'Date',       value: meeting?.meeting_date ? fmtDate(meeting.meeting_date) : '—' },
          { icon: 'time-outline',      label: 'Time',       value: meeting?.time || '—' },
          { icon: 'hourglass-outline', label: 'Duration',   value: meeting?.duration_minutes ? `${meeting.duration_minutes} min` : '—' },
          { icon: 'repeat-outline',    label: 'Type',       value: meeting?.type || 'One-time' },
          { icon: 'location-outline',  label: 'Location',   value: meeting?.location || 'Virtual' },
          { icon: 'people-outline',    label: 'Invited',    value: `${participants.length} participant${participants.length !== 1 ? 's' : ''}` },
        ].map(r => r.value && r.value !== '—' ? (
          <View key={r.label} style={St.infoRow}>
            <View style={St.infoIcon}><Ionicons name={r.icon} size={16} color="#0e4e96" /></View>
            <Text style={St.infoLabel}>{r.label}</Text>
            <Text style={{ flex: 1, fontSize: 14, color: '#263238', fontWeight: '500' }} numberOfLines={2}>{r.value}</Text>
          </View>
        ) : null)}
      </View>

      {meeting?.agenda ? (
        <View style={St.infoCard}>
          <Text style={[St.infoTitle, { marginBottom: 10 }]}>Agenda</Text>
          <Text style={{ fontSize: 14, color: '#546E7A', lineHeight: 22 }}>{meeting.agenda}</Text>
        </View>
      ) : null}

      {meeting?.location?.startsWith('http') ? (
        <View style={St.infoCard}>
          <Text style={[St.infoTitle, { marginBottom: 10 }]}>Meeting Link</Text>
          <TouchableOpacity onPress={() => Linking.openURL(meeting.location).catch(() => {})}
            style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Ionicons name="link" size={16} color="#0e4e96" />
            <Text style={{ flex: 1, fontSize: 13, color: '#0e4e96', textDecorationLine: 'underline' }}
              numberOfLines={2}>{meeting.location}</Text>
          </TouchableOpacity>
        </View>
      ) : null}

      {inCall && (
        <View style={St.infoCard}>
          <Text style={[St.infoTitle, { marginBottom: 10 }]}>Call Stats</Text>
          {[
            { label: 'Duration',     value: fmtDuration(duration) },
            { label: 'Participants', value: String(totalInCall) },
            { label: 'Recording',    value: recording ? `Active (${fmtDuration(recordingTime)})` : 'Off' },
            { label: 'Network',      value: NETWORK_LABEL[networkQuality] || '—' },
            { label: 'Video',        value: cameraOn ? `${cameraFacing === 'front' ? 'Front' : 'Back'} cam` : 'Off' },
            { label: 'Meeting',      value: meetingLocked ? '🔒 Locked' : 'Open' },
          ].map(s => (
            <View key={s.label} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 7,
              borderBottomWidth: 1, borderBottomColor: '#F5F5F5' }}>
              <Text style={{ fontSize: 13, color: '#78909C' }}>{s.label}</Text>
              <Text style={{ fontSize: 13, fontWeight: '700', color: '#263238' }}>{s.value}</Text>
            </View>
          ))}
        </View>
      )}

      <TouchableOpacity style={St.shareBtn} onPress={copyMeetingLink}>
        <Ionicons name="share-social-outline" size={18} color="#0e4e96" />
        <Text style={{ fontSize: 14, color: '#0e4e96', fontWeight: '700', marginLeft: 8 }}>
          Share Meeting Link
        </Text>
      </TouchableOpacity>
    </ScrollView>
  );

  // ── Tab: Notes ────────────────────────────────────────────────────────────────
  const renderNotes = () => (
    <View style={{ flex: 1, padding: 16 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: '#263238' }}>Meeting Notes</Text>
        <TouchableOpacity onPress={saveNotes}
          style={{ backgroundColor: '#0e4e96', borderRadius: 12, paddingHorizontal: 14, paddingVertical: 7 }}>
          <Text style={{ color: '#fff', fontSize: 13, fontWeight: '700' }}>Save</Text>
        </TouchableOpacity>
      </View>
      <TextInput
        style={[St.notesInput, { flex: 1 }]}
        value={notes}
        onChangeText={setNotes}
        placeholder="Add meeting notes, action items, decisions…"
        placeholderTextColor="#B0BEC5"
        multiline
        textAlignVertical="top"
      />
      {notes !== savedNotes && (
        <Text style={{ fontSize: 11, color: '#F57C00', marginTop: 6 }}>Unsaved changes</Text>
      )}
    </View>
  );

  // ── Tab: Polls ────────────────────────────────────────────────────────────────
  const renderPolls = () => (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
      <TouchableOpacity style={St.createPollBtn} onPress={openCreatePoll}>
        <Ionicons name="add-circle" size={20} color="#fff" />
        <Text style={{ color: '#fff', fontSize: 14, fontWeight: '700', marginLeft: 8 }}>Create Poll</Text>
      </TouchableOpacity>

      {polls.length === 0 && (
        <View style={{ alignItems: 'center', paddingTop: 40 }}>
          <Ionicons name="bar-chart-outline" size={48} color="#CFD8DC" />
          <Text style={{ color: '#B0BEC5', fontSize: 14, marginTop: 10 }}>No polls yet</Text>
          <Text style={{ color: '#CFD8DC', fontSize: 12, marginTop: 4 }}>Create one to get quick feedback</Text>
        </View>
      )}

      {polls.slice().reverse().map(poll => {
        const total = poll.options.reduce((s, o) => s + o.votes, 0) || 1;
        return (
          <View key={poll.id} style={St.pollCard}>
            <View style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 12 }}>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 15, fontWeight: '700', color: '#263238', lineHeight: 21 }}>
                  {poll.question}
                </Text>
                <Text style={{ fontSize: 11, color: '#90A4AE', marginTop: 4 }}>
                  by {poll.createdBy} · {fmtTime(poll.createdAt)} · {total} vote{total !== 1 ? 's' : ''}
                  {poll.closed && ' · closed'}
                </Text>
              </View>
              {!poll.closed && poll.createdBy === userName && (
                <TouchableOpacity onPress={() => closePoll(poll.id)} style={{ padding: 4 }}>
                  <Ionicons name="lock-closed-outline" size={18} color="#D32F2F" />
                </TouchableOpacity>
              )}
            </View>

            {poll.options.map((opt, i) => {
              const pct = total ? Math.round((opt.votes / total) * 100) : 0;
              const showResults = poll.voted || poll.closed;
              return (
                <TouchableOpacity key={i} disabled={showResults}
                  onPress={() => votePoll(poll.id, i)}
                  style={St.pollOption}>
                  {showResults && (
                    <View style={[St.pollBar, { width: `${pct}%` }]} />
                  )}
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 12 }}>
                    <Text style={{ fontSize: 14, color: '#263238', fontWeight: '600', flex: 1 }}>{opt.text}</Text>
                    {showResults
                      ? <Text style={{ fontSize: 14, color: '#0e4e96', fontWeight: '800' }}>{pct}%</Text>
                      : <Ionicons name="ellipse-outline" size={18} color="#CFD8DC" />
                    }
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        );
      })}
    </ScrollView>
  );

  // ── Tab: Whiteboard ──────────────────────────────────────────────────────────
  const WB_COLORS = ['#263238','#D32F2F','#1976D2','#388E3C','#F57C00','#7B1FA2','#00838F','#fff'];
  const WB_TOOLS  = [
    { id: 'pen',    icon: 'pencil',          label: 'Pen'    },
    { id: 'eraser', icon: 'eraser',           label: 'Erase'  },
    { id: 'rect',   icon: 'rectangle-outline',label: 'Rect'   },
    { id: 'circle', icon: 'circle-outline',   label: 'Circle' },
    { id: 'text',   icon: 'format-text',      label: 'Text'   },
    { id: 'arrow',  icon: 'arrow-right',      label: 'Arrow'  },
    { id: 'select', icon: 'cursor-default',   label: 'Select' },
  ];

  const renderWhiteboard = () => {
    const canvasH = 300;
    const scaleX  = width / 1920;
    const scaleY  = canvasH / 1080;

    const renderEl = (el, idx) => {
      const sx = (v) => v * scaleX;
      const sy = (v) => v * scaleY;
      const color = el.style?.stroke || '#263238';
      const sw    = Math.max(1, (el.style?.strokeWidth || 2) * scaleX * 300);

      if (el.type === 'text') return (
        <View key={el.id || idx} style={{ position: 'absolute', left: sx(el.x), top: sy(el.y) }}
          pointerEvents={wbTool === 'select' ? 'box-only' : 'none'}>
          <Text style={{ color, fontSize: sy(el.style?.fontSize || 18), fontWeight: '700' }}
            onPress={() => wbTool === 'select' && setWbSelected(el)}>
            {el.text}
          </Text>
        </View>
      );
      if (el.type === 'rectangle') return (
        <View key={el.id || idx} style={{
          position: 'absolute', left: sx(el.x), top: sy(el.y),
          width: sx(el.data?.width || 100), height: sy(el.data?.height || 60),
          borderWidth: sw, borderColor: color,
          backgroundColor: el.style?.fill && el.style.fill !== 'transparent' ? el.style.fill : 'transparent',
        }} pointerEvents="none" />
      );
      if (el.type === 'circle') return (
        <View key={el.id || idx} style={{
          position: 'absolute',
          left: sx(el.x - (el.data?.radius || 40)),
          top:  sy(el.y - (el.data?.radius || 40)),
          width: sx((el.data?.radius || 40) * 2),
          height: sy((el.data?.radius || 40) * 2),
          borderRadius: 9999, borderWidth: sw, borderColor: color,
          backgroundColor: el.style?.fill && el.style.fill !== 'transparent' ? el.style.fill : 'transparent',
        }} pointerEvents="none" />
      );
      if (el.type === 'path') {
        const pts = (el.data?.path || '').replace(/M |L /g,'').trim().split(' ');
        const pairs = [];
        for (let i = 0; i < pts.length - 1; i += 2) {
          pairs.push({ x: parseFloat(pts[i]), y: parseFloat(pts[i+1]) });
        }
        return pairs.slice(0, -1).map((pt, i) => {
          const nx = pairs[i+1];
          if (!nx) return null;
          const dx = sx(nx.x) - sx(pt.x), dy = sy(nx.y) - sy(pt.y);
          const len = Math.hypot(dx, dy);
          const angle = Math.atan2(dy, dx) * (180 / Math.PI);
          return (
            <View key={`${el.id}_${i}`} style={{
              position: 'absolute', left: sx(pt.x), top: sy(pt.y),
              width: len, height: sw,
              backgroundColor: color,
              transform: [{ rotate: `${angle}deg` }],
              transformOrigin: 'left center',
            }} pointerEvents="none" />
          );
        });
      }
      if (el.type === 'arrow') {
        const x2 = el.data?.x2 || el.x + 100, y2 = el.data?.y2 || el.y;
        const dx = sx(x2) - sx(el.x), dy = sy(y2) - sy(el.y);
        const len = Math.hypot(dx, dy);
        const angle = Math.atan2(dy, dx) * (180 / Math.PI);
        return (
          <View key={el.id || idx} style={{
            position: 'absolute', left: sx(el.x), top: sy(el.y) - sw / 2,
            width: len, height: sw * 2, backgroundColor: color,
            transform: [{ rotate: `${angle}deg` }],
            transformOrigin: 'left center',
            alignItems: 'flex-end', justifyContent: 'center',
          }} pointerEvents="none">
            <View style={{ width: 0, height: 0, borderLeftWidth: 8, borderLeftColor: color,
              borderTopWidth: 5, borderTopColor: 'transparent',
              borderBottomWidth: 5, borderBottomColor: 'transparent' }} />
          </View>
        );
      }
      return null;
    };

    const panResponder = PanResponder.create({
      onStartShouldSetPanResponder: () => !wbLocked,
      onMoveShouldSetPanResponder: () => !wbLocked,
      onPanResponderGrant: (evt) => {
        if (wbTool === 'select') return;
        setWbDrawing(true);
        const { locationX: x, locationY: y } = evt.nativeEvent;
        const scX = x / scaleX, scY = y / scaleY;
        if (wbTool === 'text') {
          setWbTextPos({ x: scX, y: scY });
          setWbTextInput('');
          setWbTextModal(true);
        } else {
          setWbPath([{ x: scX, y: scY }]);
        }
      },
      onPanResponderMove: (evt) => {
        if (wbTool === 'select' || wbTool === 'text') return;
        const { locationX: x, locationY: y } = evt.nativeEvent;
        setWbPath(prev => [...prev, { x: x / scaleX, y: y / scaleY }]);
      },
      onPanResponderRelease: () => { finishDraw(); },
    });

    return (
      <View style={{ flex: 1, backgroundColor: '#fff' }}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}
          style={{ backgroundColor: '#F5F7FA', maxHeight: 52, borderBottomWidth: 1, borderBottomColor: '#ECEFF1' }}
          contentContainerStyle={{ paddingHorizontal: 8, alignItems: 'center', paddingVertical: 6 }}>
          {WB_TOOLS.map(t => (
            <TouchableOpacity key={t.id}
              onPress={() => {
                setWbTool(t.id);
                if (t.id === 'rect') addShapeAtCenter('rect');
                else if (t.id === 'circle') addShapeAtCenter('circle');
                else if (t.id === 'arrow') addShapeAtCenter('arrow');
              }}
              style={{
                width: 38, height: 38, borderRadius: 8, alignItems: 'center', justifyContent: 'center',
                marginRight: 4, backgroundColor: wbTool === t.id ? '#0e4e96' : '#fff',
                borderWidth: 1, borderColor: wbTool === t.id ? '#0e4e96' : '#E0E0E0',
              }}>
              <MaterialCommunityIcons name={t.icon} size={18} color={wbTool === t.id ? '#fff' : '#546E7A'} />
            </TouchableOpacity>
          ))}
          <View style={{ width: 1, height: 30, backgroundColor: '#E0E0E0', marginHorizontal: 6 }} />
          {[1, 2, 4, 7].map(w => (
            <TouchableOpacity key={w} onPress={() => setWbStroke(w)}
              style={{ width: 34, height: 34, borderRadius: 17, alignItems: 'center', justifyContent: 'center',
                marginRight: 4, backgroundColor: wbStroke === w ? '#E3F2FD' : '#fff',
                borderWidth: 1, borderColor: wbStroke === w ? '#0e4e96' : '#E0E0E0' }}>
              <View style={{ width: w * 3, height: w * 3, borderRadius: w * 1.5, backgroundColor: wbColor }} />
            </TouchableOpacity>
          ))}
          <View style={{ width: 1, height: 30, backgroundColor: '#E0E0E0', marginHorizontal: 6 }} />
          {[
            { icon: 'undo', action: wbUndo,       label: 'Undo',   disabled: !wbHistory.length },
            { icon: 'redo', action: wbRedoAction,  label: 'Redo',   disabled: !wbRedo.length },
            { icon: 'delete-sweep', action: wbClear, label: 'Clear', disabled: false },
            { icon: 'export',       action: wbExport, label: 'Export', disabled: !wbId },
            { icon: wbLocked ? 'lock' : 'lock-open', action: () => setWbLocked(v => !v),
              label: wbLocked ? 'Locked' : 'Lock', disabled: false },
          ].map(a => (
            <TouchableOpacity key={a.icon} onPress={a.action} disabled={a.disabled}
              style={{ width: 34, height: 34, borderRadius: 8, alignItems: 'center', justifyContent: 'center',
                marginRight: 4, backgroundColor: '#fff', borderWidth: 1, borderColor: '#E0E0E0',
                opacity: a.disabled ? 0.3 : 1 }}>
              <MaterialCommunityIcons name={a.icon} size={17} color={wbLocked && a.icon === 'lock' ? '#D32F2F' : '#546E7A'} />
            </TouchableOpacity>
          ))}
        </ScrollView>

        <View style={{ flexDirection: 'row', backgroundColor: '#FAFAFA', paddingHorizontal: 10,
          paddingVertical: 5, borderBottomWidth: 1, borderBottomColor: '#ECEFF1', alignItems: 'center', gap: 6 }}>
          <Text style={{ fontSize: 10, color: '#90A4AE', fontWeight: '700', marginRight: 4 }}>COLOUR</Text>
          {WB_COLORS.map(c => (
            <TouchableOpacity key={c} onPress={() => setWbColor(c)}
              style={{ width: 22, height: 22, borderRadius: 11, backgroundColor: c,
                borderWidth: wbColor === c ? 2.5 : 1,
                borderColor: wbColor === c ? '#0e4e96' : '#CFD8DC' }} />
          ))}
          <Text style={{ fontSize: 10, color: '#90A4AE', fontWeight: '700', marginLeft: 10, marginRight: 4 }}>FILL</Text>
          {['none', '#fff', '#FFF9C4', '#E3F2FD', '#E8F5E9', '#FFEBEE'].map(c => (
            <TouchableOpacity key={c} onPress={() => setWbFill(c)}
              style={{ width: 22, height: 22, borderRadius: 4,
                backgroundColor: c === 'none' ? 'transparent' : c,
                borderWidth: wbFill === c ? 2.5 : 1,
                borderColor: wbFill === c ? '#0e4e96' : '#CFD8DC' }}>
              {c === 'none' && <Text style={{ fontSize: 11, color: '#D32F2F', textAlign: 'center', lineHeight: 20 }}>∅</Text>}
            </TouchableOpacity>
          ))}
          {wbActiveUsers.length > 0 && (
            <View style={{ flexDirection: 'row', marginLeft: 'auto', gap: 4 }}>
              {wbActiveUsers.slice(0, 3).map((u, i) => (
                <View key={u.id || i} style={{ width: 22, height: 22, borderRadius: 11,
                  backgroundColor: colorFor(u.name || ''), alignItems: 'center', justifyContent: 'center' }}>
                  <Text style={{ fontSize: 9, color: '#fff', fontWeight: '800' }}>
                    {(u.name || '?')[0].toUpperCase()}
                  </Text>
                </View>
              ))}
            </View>
          )}
        </View>

        {wbLoading
          ? <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
              <ActivityIndicator color="#0e4e96" />
              <Text style={{ color: '#78909C', marginTop: 8, fontSize: 13 }}>Loading whiteboard…</Text>
            </View>
          : <View
              style={{ flex: 1, backgroundColor: '#fff', position: 'relative', overflow: 'hidden' }}
              {...panResponder.panHandlers}>
              {Array.from({ length: 25 }).map((_, i) => (
                <View key={`vg${i}`} style={{
                  position: 'absolute', left: i * (width / 24), top: 0, bottom: 0,
                  width: 0.5, backgroundColor: '#F0F4F8' }} pointerEvents="none" />
              ))}
              {Array.from({ length: 15 }).map((_, i) => (
                <View key={`hg${i}`} style={{
                  position: 'absolute', top: i * (canvasH / 14), left: 0, right: 0,
                  height: 0.5, backgroundColor: '#F0F4F8' }} pointerEvents="none" />
              ))}
              {wbElements.map((el, i) => renderEl(el, i))}
              {wbDrawing && wbPath.length > 1 && wbPath.slice(0, -1).map((pt, i) => {
                const nx = wbPath[i + 1];
                if (!nx) return null;
                const dx = nx.x * scaleX - pt.x * scaleX;
                const dy = nx.y * scaleY - pt.y * scaleY;
                const len = Math.hypot(dx, dy);
                const angle = Math.atan2(dy, dx) * (180 / Math.PI);
                return (
                  <View key={i} style={{
                    position: 'absolute', left: pt.x * scaleX, top: pt.y * scaleY - wbStroke / 2,
                    width: len, height: wbStroke,
                    backgroundColor: wbTool === 'eraser' ? 'rgba(200,200,200,0.5)' : wbColor,
                    transform: [{ rotate: `${angle}deg` }],
                    transformOrigin: 'left center',
                  }} pointerEvents="none" />
                );
              })}
              {!wbElements.length && !wbDrawing && (
                <View style={{ position: 'absolute', inset: 0, alignItems: 'center', justifyContent: 'center' }}
                  pointerEvents="none">
                  <MaterialCommunityIcons name="draw" size={40} color="#E0E0E0" />
                  <Text style={{ color: '#CFD8DC', fontSize: 13, marginTop: 8, fontWeight: '600' }}>
                    {wbLocked ? '🔒 Board locked' : 'Tap to draw · Use toolbar to add shapes'}
                  </Text>
                </View>
              )}
              {wbSelected && (
                <TouchableOpacity
                  style={{ position: 'absolute', top: 8, right: 8, backgroundColor: '#D32F2F',
                    borderRadius: 16, paddingHorizontal: 12, paddingVertical: 6, flexDirection: 'row',
                    alignItems: 'center', gap: 5 }}
                  onPress={() => wbDeleteElement(wbSelected.id)}>
                  <MaterialCommunityIcons name="delete" size={14} color="#fff" />
                  <Text style={{ fontSize: 12, color: '#fff', fontWeight: '700' }}>Delete</Text>
                </TouchableOpacity>
              )}
            </View>}

        <Modal visible={wbTextModal} transparent animationType="fade">
          <KeyboardAvoidingView style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)',
            justifyContent: 'center', alignItems: 'center' }}
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
            <View style={{ backgroundColor: '#fff', borderRadius: 16, padding: 20, width: width * 0.85 }}>
              <Text style={{ fontSize: 15, fontWeight: '800', color: '#263238', marginBottom: 12 }}>Add Text</Text>
              <TextInput
                style={{ backgroundColor: '#F5F7FA', borderRadius: 10, padding: 12, fontSize: 15,
                  color: '#263238', borderWidth: 1.5, borderColor: '#CFD8DC', marginBottom: 14 }}
                value={wbTextInput}
                onChangeText={setWbTextInput}
                placeholder="Type your text…"
                placeholderTextColor="#B0BEC5"
                autoFocus
                multiline
              />
              <View style={{ flexDirection: 'row', gap: 10 }}>
                <TouchableOpacity style={{ flex: 1, padding: 12, borderRadius: 10,
                  backgroundColor: '#F5F7FA', alignItems: 'center' }}
                  onPress={() => { setWbTextModal(false); setWbTextInput(''); }}>
                  <Text style={{ color: '#78909C', fontWeight: '700' }}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity style={{ flex: 1, padding: 12, borderRadius: 10,
                  backgroundColor: '#0e4e96', alignItems: 'center' }}
                  onPress={addTextElement}>
                  <Text style={{ color: '#fff', fontWeight: '700' }}>Add</Text>
                </TouchableOpacity>
              </View>
            </View>
          </KeyboardAvoidingView>
        </Modal>
      </View>
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  // VIDEO GRID / SPEAKER VIEW
  // ════════════════════════════════════════════════════════════════════════════

  // Single video tile (camera if self+enabled+permitted, otherwise Avatar)
  const renderVideoTile = ({ id, name, muted: tileMuted, isMe, speakingNow, handUp, network, full, small }) => {
    const tileW = full ? width - 16 : small ? 110 : (width - 20) / 2;
    const tileH = full ? height * 0.5 : small ? 150 : (height * 0.4) / 2;
    return (
      <Pressable
        key={id}
        onPress={() => isMe ? togglePin('me') : togglePin(id)}
        style={[St.videoTile, {
          width: tileW, height: tileH,
          borderColor: speakingNow ? '#69F0AE' : 'transparent',
          borderWidth: speakingNow ? 2 : 0,
        }]}>
        {isMe ? (
          cameraOn && CAMERA_AVAILABLE && permission?.granted ? (
            <CameraView
              style={[{ flex: 1, width: '100%', height: '100%' },
                mirrorSelf && cameraFacing === 'front' ? { transform: [{ scaleX: -1 }] } : null]}
              facing={cameraFacing}
              mute
            />
          ) : (
            <LinearGradient colors={['#1a237e','#283593']} style={St.videoTileInner}>
              <Avatar name={name} size={small ? 36 : 52} speaking={speakingNow} muted={tileMuted} />
            </LinearGradient>
          )
        ) : (
          <LinearGradient colors={[colorFor(name) + 'AA', colorFor(name) + '66']}
            style={St.videoTileInner}>
            <Avatar name={name} size={small ? 36 : 52} speaking={speakingNow} muted={tileMuted} />
          </LinearGradient>
        )}

        {/* Name + indicators overlay */}
        <View style={St.tileBottomBar}>
          <Text style={St.tileLabel} numberOfLines={1}>
            {isMe ? `${name} (You)` : name}{pinnedId === id ? ' 📌' : ''}
          </Text>
          {speakingNow && !isMe && (
            <AudioBars levelAnim={audioLevelAnim} color="#69F0AE" height={12} />
          )}
        </View>

        {tileMuted && (
          <View style={St.mutedBanner}>
            <Ionicons name="mic-off" size={12} color="#fff" />
          </View>
        )}
        {handUp && (
          <View style={[St.mutedBanner, { right: 'auto', left: 8, backgroundColor: '#F57C00' }]}>
            <Text style={{ fontSize: 12 }}>✋</Text>
          </View>
        )}
        {network != null && network < 3 && (
          <View style={[St.mutedBanner, { right: 'auto', left: 8, top: 8, bottom: 'auto',
            backgroundColor: NETWORK_COLOR[network] || '#90A4AE', flexDirection: 'row', alignItems: 'center', gap: 4 }]}>
            <NetworkBars quality={network} size={9} />
            <Text style={{ fontSize: 9, color: '#fff', fontWeight: '700' }}>{NETWORK_LABEL[network]}</Text>
          </View>
        )}
        {isMe && cameraOn && CAMERA_AVAILABLE && (
          <TouchableOpacity onPress={flipCamera} style={St.flipCamBtn}>
            <Ionicons name="camera-reverse" size={16} color="#fff" />
          </TouchableOpacity>
        )}
      </Pressable>
    );
  };

  // Grid view: me + up to 3 other tiles
  const renderGridView = () => (
    <View style={{ flex: 1, padding: 8 }}>
      {joinedParticipants.length === 0 ? (
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          {cameraOn && CAMERA_AVAILABLE && permission?.granted ? (
            <View style={{ width: 180, height: 240, borderRadius: 16, overflow: 'hidden', marginBottom: 16 }}>
              <CameraView
                style={[{ flex: 1 },
                  mirrorSelf && cameraFacing === 'front' ? { transform: [{ scaleX: -1 }] } : null]}
                facing={cameraFacing}
                mute
              />
            </View>
          ) : (
            <Avatar name={userName} size={90} speaking={speaking === 'me'} muted={!micOn} />
          )}
          <Text style={{ color: '#fff', fontSize: 16, fontWeight: '700', marginTop: 16 }}>
            Waiting for others to join…
          </Text>
          <ActivityIndicator color="rgba(255,255,255,0.5)" style={{ marginTop: 12 }} />
        </View>
      ) : (
        <View style={{ flex: 1, flexDirection: 'row', flexWrap: 'wrap', gap: 4 }}>
          {renderVideoTile({ id: 'me', name: userName, muted: !micOn, isMe: true,
            speakingNow: speaking === 'me' })}
          {joinedParticipants.slice(0, 3).map(p => renderVideoTile({
            id: p.id, name: p.name, muted: p.muted, isMe: false,
            speakingNow: speaking === p.id, handUp: p.handRaised, network: p.networkQuality,
          }))}
        </View>
      )}
    </View>
  );

  // Speaker view: one large tile + thumbnail strip
  const renderSpeakerView = () => {
    const featured = pinnedPerson || (
      speaking === 'me'
        ? { id: 'me', name: userName, muted: !micOn, isMe: true }
        : joinedParticipants.find(p => p.id === speaking) || joinedParticipants[0] || {
            id: 'me', name: userName, muted: !micOn, isMe: true,
          }
    );
    const thumbs = [
      ...(featured.id !== 'me' ? [{ id: 'me', name: userName, muted: !micOn, isMe: true }] : []),
      ...joinedParticipants.filter(p => p.id !== featured.id),
    ];

    return (
      <View style={{ flex: 1, padding: 8 }}>
        <View style={{ flex: 1 }}>
          {renderVideoTile({
            id: featured.id, name: featured.name, muted: featured.muted,
            isMe: !!featured.isMe, speakingNow: speaking === featured.id,
            handUp: featured.handRaised, network: featured.networkQuality, full: true,
          })}
        </View>
        {thumbs.length > 0 && (
          <ScrollView horizontal showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ gap: 6, paddingTop: 8, paddingHorizontal: 4 }}>
            {thumbs.map(p => renderVideoTile({
              id: p.id, name: p.name, muted: p.muted, isMe: !!p.isMe,
              speakingNow: speaking === p.id, handUp: p.handRaised, network: p.networkQuality, small: true,
            }))}
          </ScrollView>
        )}
      </View>
    );
  };

  // PiP self-view (shown when someone else is pinned and not you)
  const renderPiPSelfView = () => {
    if (!pinnedId || pinnedId === 'me') return null;
    return (
      <Animated.View
        style={[St.pip, {
          transform: [{ translateX: pipPos.x }, { translateY: pipPos.y }],
        }]}
        {...pipPanResponder.panHandlers}>
        {cameraOn && CAMERA_AVAILABLE && permission?.granted ? (
          <CameraView
            style={[{ flex: 1, width: '100%', height: '100%' },
              mirrorSelf && cameraFacing === 'front' ? { transform: [{ scaleX: -1 }] } : null]}
            facing={cameraFacing}
            mute
          />
        ) : (
          <LinearGradient colors={['#1a237e','#283593']} style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
            <Avatar name={userName} size={36} muted={!micOn} />
          </LinearGradient>
        )}
        <View style={{ position: 'absolute', bottom: 4, left: 6, right: 6, flexDirection: 'row',
          alignItems: 'center', justifyContent: 'space-between' }}>
          <Text style={{ color: '#fff', fontSize: 10, fontWeight: '700' }} numberOfLines={1}>You</Text>
          {!micOn && <Ionicons name="mic-off" size={11} color="#fff" />}
        </View>
      </Animated.View>
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  // LOBBY / ENDED / IN-CALL
  // ════════════════════════════════════════════════════════════════════════════

  const renderLobby = () => (
    <View style={{ flex: 1 }}>
      <LinearGradient colors={['#0e4e96','#1565C0']} style={{ padding: 28, alignItems: 'center' }}>
        <View style={{ width: 80, height: 80, borderRadius: 40, backgroundColor: 'rgba(255,255,255,0.15)',
          alignItems: 'center', justifyContent: 'center', marginBottom: 16, overflow: 'hidden' }}>
          {cameraOn && CAMERA_AVAILABLE && permission?.granted ? (
            <CameraView
              style={[{ width: 80, height: 80 },
                mirrorSelf && cameraFacing === 'front' ? { transform: [{ scaleX: -1 }] } : null]}
              facing={cameraFacing}
              mute
            />
          ) : (
            <Ionicons name="videocam" size={38} color="#fff" />
          )}
        </View>
        <Text style={{ fontSize: 22, fontWeight: '900', color: '#fff', textAlign: 'center' }}>
          {meeting?.title || 'Meeting'}
        </Text>
        <Text style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', marginTop: 6, textAlign: 'center' }}>
          {meeting?.meeting_date} · {meeting?.time}
        </Text>
        {participants.length > 0 && (
          <Text style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', marginTop: 4 }}>
            {participants.length} participant{participants.length !== 1 ? 's' : ''} invited
          </Text>
        )}
      </LinearGradient>

      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 20 }}>
        {/* Big camera preview if camera is on */}
        {cameraOn && CAMERA_AVAILABLE && permission?.granted && (
          <View style={{ height: 240, borderRadius: 16, overflow: 'hidden', marginBottom: 16,
            backgroundColor: '#1a237e' }}>
            <CameraView
              style={[{ flex: 1 },
                mirrorSelf && cameraFacing === 'front' ? { transform: [{ scaleX: -1 }] } : null]}
              facing={cameraFacing}
              mute
            />
            <TouchableOpacity onPress={flipCamera} style={St.flipCamBtnLarge}>
              <Ionicons name="camera-reverse" size={20} color="#fff" />
            </TouchableOpacity>
          </View>
        )}

        {/* Device controls */}
        <View style={St.deviceRow}>
          <TouchableOpacity style={[St.deviceBtn, micOn && St.deviceBtnActive]}
            onPress={() => setMicOn(v => !v)}>
            <Ionicons name={micOn ? 'mic' : 'mic-off'} size={22} color={micOn ? '#0e4e96' : '#D32F2F'} />
            <Text style={[St.deviceLabel, micOn && { color: '#0e4e96' }]}>
              {micOn ? 'Mic On' : 'Mic Off'}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity style={[St.deviceBtn, cameraOn && St.deviceBtnActive]}
            onPress={toggleCamera}>
            <Ionicons name={cameraOn ? 'videocam' : 'videocam-off'} size={22}
              color={cameraOn ? '#0e4e96' : '#9E9E9E'} />
            <Text style={[St.deviceLabel, cameraOn && { color: '#0e4e96' }]}>
              {cameraOn ? 'Camera On' : 'Camera Off'}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity style={[St.deviceBtn, speakerOn && St.deviceBtnActive]}
            onPress={() => setSpeakerOn(v => !v)}>
            <Ionicons name={speakerOn ? 'volume-high' : 'volume-mute'} size={22}
              color={speakerOn ? '#0e4e96' : '#9E9E9E'} />
            <Text style={[St.deviceLabel, speakerOn && { color: '#0e4e96' }]}>
              {speakerOn ? 'Speaker' : 'Earpiece'}
            </Text>
          </TouchableOpacity>
        </View>

        {!CAMERA_AVAILABLE && (
          <View style={St.warningCard}>
            <Ionicons name="information-circle" size={16} color="#F57C00" />
            <Text style={{ fontSize: 12, color: '#E65100', flex: 1, marginLeft: 8 }}>
              expo-camera not installed — install for real video. Run: npx expo install expo-camera
            </Text>
          </View>
        )}
        {CAMERA_AVAILABLE && !permission?.granted && cameraOn && (
          <View style={St.warningCard}>
            <Ionicons name="alert-circle" size={16} color="#F57C00" />
            <Text style={{ fontSize: 12, color: '#E65100', flex: 1, marginLeft: 8 }}>
              Camera permission needed for real video preview.
            </Text>
          </View>
        )}

        {meeting?.agenda ? (
          <View style={St.lobbyCard}>
            <Text style={St.lobbyCardTitle}>Agenda</Text>
            <Text style={{ fontSize: 13, color: '#546E7A', lineHeight: 20 }}>{meeting.agenda}</Text>
          </View>
        ) : null}

        {participants.length > 0 && (
          <View style={St.lobbyCard}>
            <Text style={St.lobbyCardTitle}>Participants ({participants.length})</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
              {participants.slice(0, 8).map((p, i) => (
                <View key={p.id || i} style={{ alignItems: 'center', gap: 5 }}>
                  <Avatar name={p.name} size={40} color={colorFor(p.name)} />
                  <Text style={{ fontSize: 10, color: '#78909C', maxWidth: 50, textAlign: 'center' }}
                    numberOfLines={1}>{p.name.split(' ')[0]}</Text>
                </View>
              ))}
              {participants.length > 8 && (
                <View style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: '#ECEFF1',
                  alignItems: 'center', justifyContent: 'center' }}>
                  <Text style={{ fontSize: 11, color: '#78909C', fontWeight: '700' }}>+{participants.length - 8}</Text>
                </View>
              )}
            </View>
          </View>
        )}

        <TouchableOpacity style={[St.joinCallBtn, joining && { opacity: 0.7 }]}
          onPress={joinCall} disabled={joining}>
          {joining
            ? <ActivityIndicator color="#fff" size={22} />
            : <>
                <Ionicons name="videocam" size={22} color="#fff" />
                <Text style={{ fontSize: 17, fontWeight: '800', color: '#fff', marginLeft: 10 }}>
                  Join Meeting
                </Text>
              </>}
        </TouchableOpacity>

        <TouchableOpacity onPress={() => navigation.goBack()}
          style={{ alignItems: 'center', paddingVertical: 16 }}>
          <Text style={{ fontSize: 14, color: '#78909C' }}>Cancel</Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );

  const renderEnded = () => (
    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32 }}>
      <LinearGradient colors={['#E8F5E9','#C8E6C9']} style={{ width: 100, height: 100, borderRadius: 50,
        alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
        <Ionicons name="checkmark-circle" size={52} color="#388E3C" />
      </LinearGradient>
      <Text style={{ fontSize: 24, fontWeight: '900', color: '#263238', marginBottom: 8 }}>
        Call Ended
      </Text>
      <Text style={{ fontSize: 15, color: '#78909C', marginBottom: 4 }}>Duration: {fmtDuration(duration)}</Text>
      <Text style={{ fontSize: 14, color: '#90A4AE', marginBottom: 32 }}>
        {totalInCall} participant{totalInCall !== 1 ? 's' : ''}
      </Text>
      {notes ? (
        <View style={[St.lobbyCard, { width: '100%', marginBottom: 16 }]}>
          <Text style={St.lobbyCardTitle}>Meeting Notes Saved</Text>
          <Text style={{ fontSize: 13, color: '#546E7A', lineHeight: 20 }} numberOfLines={3}>{notes}</Text>
        </View>
      ) : null}
      <TouchableOpacity style={[St.joinCallBtn, { backgroundColor: '#0e4e96', width: '100%' }]}
        onPress={() => navigation.goBack()}>
        <Text style={{ fontSize: 16, fontWeight: '800', color: '#fff' }}>Back to Team</Text>
      </TouchableOpacity>
      <TouchableOpacity onPress={() => { setCallPhase('lobby'); setInCall(false); setDuration(0); setTimeWarningShown(false); }}
        style={{ marginTop: 12 }}>
        <Text style={{ fontSize: 14, color: '#0e4e96', fontWeight: '600' }}>Rejoin</Text>
      </TouchableOpacity>
    </View>
  );

  // ── Live captions overlay ────────────────────────────────────────────────────
  const renderCaptionsOverlay = () => {
    if (!captionsEnabled || captions.length === 0) return null;
    return (
      <View style={St.captionsOverlay} pointerEvents="none">
        {captions.slice(-2).map(c => (
          <View key={c.id} style={St.captionBubble}>
            <Text style={{ color: '#69F0AE', fontSize: 11, fontWeight: '800' }}>{c.speaker}</Text>
            <Text style={{ color: '#fff', fontSize: 13, marginTop: 2 }}>{c.text}</Text>
          </View>
        ))}
      </View>
    );
  };

  // ── In-call UI ────────────────────────────────────────────────────────────────
  const renderInCall = () => (
    <View style={{ flex: 1, backgroundColor: '#1a1a2e' }}>
      {reactions.map(r => (
        <View key={r.id} style={[St.floatingReaction, { bottom: r.y, left: r.x }]}>
          <Text style={{ fontSize: 32 }}>{r.emoji}</Text>
          <Text style={{ fontSize: 11, color: '#fff', textAlign: 'center' }}>{r.name}</Text>
        </View>
      ))}

      <TouchableOpacity style={{ flex: 1 }} activeOpacity={1} onPress={touchScreen}>
        {viewMode === 'speaker' ? renderSpeakerView() : renderGridView()}

        {/* PiP self-view when someone else is pinned */}
        {renderPiPSelfView()}

        {/* Captions overlay */}
        {renderCaptionsOverlay()}

        {/* Top status bar */}
        {showControls && (
          <View style={St.statusBar}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <NetworkBars quality={networkQuality} size={12} />
              <Text style={{ color: '#fff', fontSize: 13, fontWeight: '700' }}>{fmtDuration(duration)}</Text>
              {recording && (
                <View style={St.recBadge}>
                  <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: '#fff' }} />
                  <Text style={{ color: '#fff', fontSize: 11, fontWeight: '800', marginLeft: 4 }}>
                    REC {fmtDuration(recordingTime)}
                  </Text>
                </View>
              )}
              {meetingLocked && (
                <View style={[St.recBadge, { backgroundColor: '#F57C00' }]}>
                  <Ionicons name="lock-closed" size={10} color="#fff" />
                  <Text style={{ color: '#fff', fontSize: 10, fontWeight: '800', marginLeft: 3 }}>LOCKED</Text>
                </View>
              )}
            </View>
            <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13 }} numberOfLines={1}>
              {meeting?.title}
            </Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <TouchableOpacity onPress={() => setViewMode(v => v === 'grid' ? 'speaker' : 'grid')}>
                <MaterialCommunityIcons
                  name={viewMode === 'grid' ? 'view-grid' : 'view-agenda'}
                  size={20} color="rgba(255,255,255,0.85)" />
              </TouchableOpacity>
              <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
                {totalInCall}
              </Text>
            </View>
          </View>
        )}

        {/* Mic activity indicator (bottom-left when speaking & mic on) */}
        {micOn && (
          <View style={St.micActivityPanel}>
            <Ionicons name="mic" size={14} color="#69F0AE" />
            <AudioBars levelAnim={audioLevelAnim} color="#69F0AE" height={14} />
          </View>
        )}
      </TouchableOpacity>

      {/* Bottom panel: tabs + content + controls */}
      <View style={St.bottomPanel}>
        <View style={St.callTabBar}>
          {[
            { id: 'participants', icon: 'people',              label: `People (${totalInCall})` },
            { id: 'chat',         icon: 'chatbubbles',         label: 'Chat',  badge: unreadCount },
            { id: 'polls',        icon: 'bar-chart',           label: 'Polls', badge: activePoll && !activePoll.voted ? 1 : 0 },
            { id: 'whiteboard',   icon: 'brush',               label: 'Board'  },
            { id: 'info',         icon: 'information-circle',  label: 'Info'   },
            { id: 'notes',        icon: 'document-text',       label: 'Notes', dot: notes !== savedNotes },
          ].map(t => (
            <TouchableOpacity key={t.id} style={[St.callTab, activeTab === t.id && St.callTabActive]}
              onPress={() => switchTab(t.id)}>
              <View style={{ position: 'relative' }}>
                <Ionicons name={t.icon} size={18} color={activeTab === t.id ? '#0e4e96' : 'rgba(255,255,255,0.6)'} />
                {t.badge > 0 && (
                  <View style={St.badge}>
                    <Text style={{ fontSize: 9, color: '#fff', fontWeight: '800' }}>{t.badge}</Text>
                  </View>
                )}
                {t.dot && <View style={[St.badge, { backgroundColor: '#F57C00', width: 8, height: 8, top: -2, right: -2 }]} />}
              </View>
              <Text style={[St.callTabLabel, activeTab === t.id && { color: '#0e4e96' }]} numberOfLines={1}>
                {t.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        <View style={{ height: activeTab === 'whiteboard' ? 320 : 200, backgroundColor: '#fff' }}>
          {activeTab === 'participants' && renderParticipants()}
          {activeTab === 'chat'         && renderChat()}
          {activeTab === 'polls'        && renderPolls()}
          {activeTab === 'whiteboard'   && renderWhiteboard()}
          {activeTab === 'info'         && renderInfo()}
          {activeTab === 'notes'        && renderNotes()}
        </View>

        {/* Controls */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ paddingHorizontal: 6, gap: 6, alignItems: 'center' }}
          style={{ backgroundColor: 'rgba(0,0,0,0.3)', paddingVertical: 12 }}>
          <ControlBtn icon={micOn ? 'mic' : 'mic-off'} label={micOn ? 'Mute' : 'Unmute'}
            active={micOn} onPress={() => setMicOn(v => !v)} />
          <ControlBtn icon={cameraOn ? 'videocam' : 'videocam-off'} label={cameraOn ? 'Camera' : 'No Cam'}
            active={cameraOn} onPress={toggleCamera} />
          {cameraOn && CAMERA_AVAILABLE && (
            <ControlBtn icon="camera-reverse" label="Flip" active={false} onPress={flipCamera} />
          )}
          <ControlBtn icon={speakerOn ? 'volume-high' : 'volume-mute'} label="Speaker"
            active={speakerOn} onPress={() => setSpeakerOn(v => !v)} />
          <ControlBtn icon="hand-right" label={handRaised ? 'Lower' : 'Raise'}
            active={handRaised}
            onPress={() => {
              setHandRaised(v => !v);
              if (!handRaised) addSystemMessage(`${userName} raised their hand`);
            }} />
          <ControlBtn icon="happy" label="React" active={showReactions}
            onPress={() => setShowReactions(v => !v)} />
          <ControlBtn icon="closed-captioning" label="CC" active={captionsEnabled}
            onPress={() => setCaptionsEnabled(v => !v)} />
          <ControlBtn icon={recording ? 'radio-button-on' : 'radio-button-off'}
            label={recording ? 'Stop' : 'Record'}
            active={recording} onPress={toggleRecording} />
          <ControlBtn icon="share-social" label="Invite" active={false} onPress={copyMeetingLink} />
          <ControlBtn icon="settings-outline" label="Settings" active={showSettings}
            onPress={() => setShowSettings(true)} />
          <ControlBtn icon="ellipsis-horizontal" label="More" active={showMoreMenu}
            onPress={() => setShowMoreMenu(true)} />
          <ControlBtn icon="call" label="End" danger onPress={() => setShowEndModal(true)} />
        </ScrollView>

        {/* Inline reactions picker (when React control is pressed and chat isn't open) */}
        {showReactions && activeTab !== 'chat' && (
          <View style={St.reactionPickerFloating}>
            {REACTIONS.map(e => (
              <TouchableOpacity key={e} onPress={() => sendReaction(e)} style={{ padding: 6 }}>
                <Text style={{ fontSize: 26 }}>{e}</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </View>
    </View>
  );

  // ── End Call Modal ────────────────────────────────────────────────────────────
  const renderEndModal = () => (
    <Modal visible={showEndModal} transparent animationType="fade">
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'flex-end' }}>
        <View style={{ backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 28 }}>
          <Text style={{ fontSize: 18, fontWeight: '800', color: '#263238', textAlign: 'center', marginBottom: 6 }}>
            Leave Meeting?
          </Text>
          <Text style={{ fontSize: 14, color: '#78909C', textAlign: 'center', marginBottom: 24 }}>
            Duration so far: {fmtDuration(duration)}
          </Text>
          <TouchableOpacity style={[St.endModalBtn, { backgroundColor: '#FFEBEE', borderColor: '#FFCDD2', marginBottom: 10 }]}
            onPress={() => leaveCall(false)} disabled={leaving}>
            <Ionicons name="log-out-outline" size={20} color="#D32F2F" />
            <Text style={{ fontSize: 15, fontWeight: '700', color: '#D32F2F', marginLeft: 8 }}>
              Leave Call
            </Text>
          </TouchableOpacity>
          <TouchableOpacity style={[St.endModalBtn, { backgroundColor: '#D32F2F', borderColor: '#D32F2F', marginBottom: 10 }]}
            onPress={() => leaveCall(true)} disabled={leaving}>
            {leaving ? <ActivityIndicator color="#fff" size={18} /> : <>
              <Ionicons name="stop-circle-outline" size={20} color="#fff" />
              <Text style={{ fontSize: 15, fontWeight: '700', color: '#fff', marginLeft: 8 }}>
                End for Everyone
              </Text>
            </>}
          </TouchableOpacity>
          <TouchableOpacity onPress={() => setShowEndModal(false)}
            style={{ alignItems: 'center', paddingVertical: 12 }}>
            <Text style={{ fontSize: 15, color: '#78909C', fontWeight: '600' }}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  // ── Invite Modal ──────────────────────────────────────────────────────────────
  const renderInviteModal = () => (
    <Modal visible={showInviteModal} transparent animationType="slide">
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' }}>
        <View style={{ backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 24 }}>
          <Text style={{ fontSize: 17, fontWeight: '800', color: '#263238', marginBottom: 16 }}>Invite to Meeting</Text>
          <TouchableOpacity style={St.inviteOption} onPress={copyMeetingLink}>
            <View style={[St.inviteIcon, { backgroundColor: '#E3F2FD' }]}>
              <Ionicons name="link" size={20} color="#1976D2" />
            </View>
            <View>
              <Text style={{ fontSize: 15, fontWeight: '700', color: '#263238' }}>Copy Meeting Link</Text>
              <Text style={{ fontSize: 12, color: '#78909C' }}>Share via any app</Text>
            </View>
          </TouchableOpacity>
          <TouchableOpacity style={St.inviteOption}
            onPress={() => {
              const link = meeting?.location || `https://meet.jobeng.ai/${meetingId || 'instant'}`;
              Linking.openURL(`mailto:?subject=${encodeURIComponent(meeting?.title || 'Meeting Invite')}&body=${encodeURIComponent(`Join meeting: ${link}`)}`).catch(() => {});
              setShowInviteModal(false);
            }}>
            <View style={[St.inviteIcon, { backgroundColor: '#E8F5E9' }]}>
              <Ionicons name="mail" size={20} color="#388E3C" />
            </View>
            <View>
              <Text style={{ fontSize: 15, fontWeight: '700', color: '#263238' }}>Send Email Invite</Text>
              <Text style={{ fontSize: 12, color: '#78909C' }}>Open mail app</Text>
            </View>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => setShowInviteModal(false)}
            style={{ alignItems: 'center', paddingVertical: 14 }}>
            <Text style={{ fontSize: 15, color: '#78909C' }}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  // ── Settings Modal ────────────────────────────────────────────────────────────
  const renderSettingsModal = () => (
    <Modal visible={showSettings} transparent animationType="slide">
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' }}>
        <View style={{ backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 24, maxHeight: '80%' }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Text style={{ fontSize: 17, fontWeight: '800', color: '#263238' }}>Settings</Text>
            <TouchableOpacity onPress={() => setShowSettings(false)}>
              <Ionicons name="close" size={24} color="#78909C" />
            </TouchableOpacity>
          </View>

          <ScrollView>
            {/* Video quality */}
            <Text style={St.settingsLabel}>Video Quality</Text>
            <View style={St.settingsRow}>
              {[
                { id: 'auto', label: 'Auto' },
                { id: 'hd',   label: 'HD 720p' },
                { id: 'sd',   label: 'SD 480p' },
              ].map(q => (
                <TouchableOpacity key={q.id} onPress={() => setVideoQuality(q.id)}
                  style={[St.settingsChoice, videoQuality === q.id && St.settingsChoiceActive]}>
                  <Text style={[St.settingsChoiceText, videoQuality === q.id && { color: '#fff' }]}>
                    {q.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* Captions language */}
            <Text style={St.settingsLabel}>Caption Language</Text>
            <View style={St.settingsRow}>
              {[
                { id: 'en', label: 'English' },
                { id: 'ar', label: 'العربية' },
                { id: 'es', label: 'Español' },
              ].map(l => (
                <TouchableOpacity key={l.id} onPress={() => setCaptionLang(l.id)}
                  style={[St.settingsChoice, captionLang === l.id && St.settingsChoiceActive]}>
                  <Text style={[St.settingsChoiceText, captionLang === l.id && { color: '#fff' }]}>
                    {l.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* Toggles */}
            {[
              { label: 'Live Captions',       value: captionsEnabled,  onChange: () => setCaptionsEnabled(v => !v) },
              { label: 'Mirror My Camera',    value: mirrorSelf,       onChange: () => setMirrorSelf(v => !v) },
              { label: 'Noise Suppression',   value: noiseSuppression, onChange: () => setNoiseSuppression(v => !v) },
              { label: 'Speaker Mode',        value: speakerOn,        onChange: () => setSpeakerOn(v => !v) },
            ].map(t => (
              <TouchableOpacity key={t.label} onPress={t.onChange} style={St.settingsToggleRow}>
                <Text style={{ fontSize: 14, color: '#263238', fontWeight: '600', flex: 1 }}>{t.label}</Text>
                <View style={[St.toggle, t.value && { backgroundColor: '#0e4e96' }]}>
                  <View style={[St.toggleHandle, t.value && { transform: [{ translateX: 18 }] }]} />
                </View>
              </TouchableOpacity>
            ))}

            <View style={{ marginTop: 16, padding: 14, backgroundColor: '#F5F7FA', borderRadius: 12 }}>
              <Text style={{ fontSize: 11, color: '#90A4AE', fontWeight: '700', marginBottom: 6 }}>DIAGNOSTICS</Text>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4 }}>
                <Text style={{ fontSize: 12, color: '#78909C' }}>Network</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <NetworkBars quality={networkQuality} />
                  <Text style={{ fontSize: 12, color: '#263238', fontWeight: '700' }}>{NETWORK_LABEL[networkQuality]}</Text>
                </View>
              </View>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4 }}>
                <Text style={{ fontSize: 12, color: '#78909C' }}>Camera Module</Text>
                <Text style={{ fontSize: 12, color: CAMERA_AVAILABLE ? '#388E3C' : '#D32F2F', fontWeight: '700' }}>
                  {CAMERA_AVAILABLE ? 'Installed' : 'Not Installed'}
                </Text>
              </View>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4 }}>
                <Text style={{ fontSize: 12, color: '#78909C' }}>Camera Permission</Text>
                <Text style={{ fontSize: 12, color: permission?.granted ? '#388E3C' : '#90A4AE', fontWeight: '700' }}>
                  {permission?.granted ? 'Granted' : permission?.status || 'Not Requested'}
                </Text>
              </View>
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  // ── More Menu Modal ───────────────────────────────────────────────────────────
  const renderMoreMenu = () => (
    <Modal visible={showMoreMenu} transparent animationType="slide">
      <TouchableOpacity style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)' }}
        activeOpacity={1} onPress={() => setShowMoreMenu(false)}>
        <View style={{ flex: 1, justifyContent: 'flex-end' }}>
          <TouchableOpacity activeOpacity={1}
            style={{ backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20 }}>
            <Text style={{ fontSize: 16, fontWeight: '800', color: '#263238', marginBottom: 16, textAlign: 'center' }}>
              More Actions
            </Text>
            {[
              { icon: 'mic-off-outline', label: 'Mute Everyone', color: '#D32F2F', onPress: muteAll },
              { icon: meetingLocked ? 'lock-open-outline' : 'lock-closed-outline',
                label: meetingLocked ? 'Unlock Meeting' : 'Lock Meeting', color: '#F57C00',
                onPress: toggleMeetingLock },
              { icon: 'bar-chart-outline', label: 'Create Poll', color: '#0e4e96',
                onPress: () => { setShowMoreMenu(false); openCreatePoll(); } },
              { icon: 'pin-outline', label: pinnedId ? 'Unpin All' : 'Pin Spotlight', color: '#7B1FA2',
                onPress: () => { setPinnedId(null); setShowMoreMenu(false); } },
              { icon: 'desktop-outline', label: screenShare ? 'Stop Sharing' : 'Share Screen', color: '#388E3C',
                onPress: () => {
                  setScreenShare(v => !v);
                  addSystemMessage(screenShare ? `${userName} stopped sharing` : `${userName} started sharing screen`);
                  setShowMoreMenu(false);
                } },
              { icon: 'document-text-outline', label: 'Open Notes', color: '#0097A7',
                onPress: () => { setActiveTab('notes'); setShowMoreMenu(false); } },
            ].map(item => (
              <TouchableOpacity key={item.label} onPress={item.onPress} style={St.moreMenuItem}>
                <View style={[St.moreMenuIcon, { backgroundColor: item.color + '15' }]}>
                  <Ionicons name={item.icon} size={20} color={item.color} />
                </View>
                <Text style={{ fontSize: 15, color: '#263238', fontWeight: '600', flex: 1 }}>{item.label}</Text>
                <Ionicons name="chevron-forward" size={18} color="#CFD8DC" />
              </TouchableOpacity>
            ))}
            <TouchableOpacity onPress={() => setShowMoreMenu(false)}
              style={{ alignItems: 'center', paddingVertical: 12, marginTop: 8 }}>
              <Text style={{ fontSize: 15, color: '#78909C', fontWeight: '600' }}>Close</Text>
            </TouchableOpacity>
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    </Modal>
  );

  // ── Create Poll Modal ─────────────────────────────────────────────────────────
  const renderCreatePollModal = () => (
    <Modal visible={showCreatePoll} transparent animationType="slide">
      <KeyboardAvoidingView style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)' }}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <View style={{ flex: 1, justifyContent: 'flex-end' }}>
          <View style={{ backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 24, maxHeight: '85%' }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Text style={{ fontSize: 17, fontWeight: '800', color: '#263238' }}>Create Poll</Text>
              <TouchableOpacity onPress={() => setShowCreatePoll(false)}>
                <Ionicons name="close" size={24} color="#78909C" />
              </TouchableOpacity>
            </View>

            <ScrollView keyboardShouldPersistTaps="handled">
              <Text style={St.settingsLabel}>Question</Text>
              <TextInput
                style={St.pollInput}
                value={pollQuestion}
                onChangeText={setPollQuestion}
                placeholder="What would you like to ask?"
                placeholderTextColor="#B0BEC5"
                maxLength={140}
              />
              <Text style={St.settingsLabel}>Options</Text>
              {pollOptions.map((opt, i) => (
                <View key={i} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                  <TextInput
                    style={[St.pollInput, { flex: 1, marginBottom: 0 }]}
                    value={opt}
                    onChangeText={(t) => setPollOptions(prev => prev.map((o, j) => j === i ? t : o))}
                    placeholder={`Option ${i + 1}`}
                    placeholderTextColor="#B0BEC5"
                    maxLength={80}
                  />
                  {pollOptions.length > 2 && (
                    <TouchableOpacity onPress={() => setPollOptions(prev => prev.filter((_, j) => j !== i))}
                      style={{ marginLeft: 8, padding: 4 }}>
                      <Ionicons name="close-circle" size={22} color="#D32F2F" />
                    </TouchableOpacity>
                  )}
                </View>
              ))}
              {pollOptions.length < 6 && (
                <TouchableOpacity onPress={() => setPollOptions(prev => [...prev, ''])}
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 8 }}>
                  <Ionicons name="add-circle" size={18} color="#0e4e96" />
                  <Text style={{ fontSize: 13, color: '#0e4e96', fontWeight: '700' }}>Add option</Text>
                </TouchableOpacity>
              )}

              <TouchableOpacity onPress={createPoll}
                style={{ backgroundColor: '#0e4e96', borderRadius: 14, paddingVertical: 14, alignItems: 'center', marginTop: 12 }}>
                <Text style={{ color: '#fff', fontWeight: '800', fontSize: 15 }}>Launch Poll</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // MAIN RETURN
  // ════════════════════════════════════════════════════════════════════════════
  if (loading) return (
    <SafeAreaView style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: '#0e4e96' }}>
      <ActivityIndicator size="large" color="#fff" />
      <Text style={{ color: 'rgba(255,255,255,0.75)', marginTop: 14, fontSize: 14 }}>Loading meeting…</Text>
    </SafeAreaView>
  );

  if (callPhase === 'ended') return (
    <SafeAreaView style={{ flex: 1, backgroundColor: '#F5F7FA' }}>
      {renderEnded()}
    </SafeAreaView>
  );

  if (inCall) return (
    <SafeAreaView style={{ flex: 1, backgroundColor: '#1a1a2e' }} edges={['top']}>
      {renderInCall()}
      {renderEndModal()}
      {renderInviteModal()}
      {renderSettingsModal()}
      {renderMoreMenu()}
      {renderCreatePollModal()}
    </SafeAreaView>
  );

  // Lobby
  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: '#F5F7FA' }}>
      <View style={St.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={St.headerBtn}>
          <Ionicons name="arrow-back" size={22} color="#0e4e96" />
        </TouchableOpacity>
        <Text style={St.headerTitle}>Video Conference</Text>
        <TouchableOpacity onPress={copyMeetingLink} style={St.headerBtn}>
          <Ionicons name="share-outline" size={22} color="#0e4e96" />
        </TouchableOpacity>
      </View>
      {renderLobby()}
      {renderInviteModal()}
    </SafeAreaView>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const St = StyleSheet.create({
  header:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 16, paddingVertical: 12, backgroundColor: '#fff',
    borderBottomWidth: 1, borderBottomColor: '#ECEFF1' },
  headerTitle:{ fontSize: 17, fontWeight: '800', color: '#263238' },
  headerBtn:  { width: 36, height: 36, borderRadius: 18, backgroundColor: '#F5F7FA',
    alignItems: 'center', justifyContent: 'center' },

  deviceRow:    { flexDirection: 'row', gap: 10, marginBottom: 16 },
  deviceBtn:    { flex: 1, backgroundColor: '#fff', borderRadius: 14, padding: 14, alignItems: 'center',
    gap: 6, borderWidth: 1.5, borderColor: '#ECEFF1' },
  deviceBtnActive: { borderColor: '#0e4e96', backgroundColor: '#E3F2FD' },
  deviceLabel:  { fontSize: 11, color: '#78909C', fontWeight: '600', textAlign: 'center' },

  warningCard:  { flexDirection: 'row', alignItems: 'center', backgroundColor: '#FFF3E0',
    borderRadius: 10, padding: 12, marginBottom: 14, borderWidth: 1, borderColor: '#FFE0B2' },

  lobbyCard:    { backgroundColor: '#fff', borderRadius: 16, padding: 16, marginBottom: 14,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.07, shadowRadius: 5, elevation: 2 },
  lobbyCardTitle: { fontSize: 13, fontWeight: '800', color: '#546E7A', textTransform: 'uppercase',
    letterSpacing: 0.8, marginBottom: 10 },

  joinCallBtn:  { backgroundColor: '#388E3C', borderRadius: 16, paddingVertical: 18,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    shadowColor: '#388E3C', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.35, shadowRadius: 10, elevation: 6 },

  // In-call
  statusBar:    { position: 'absolute', top: 0, left: 0, right: 0, flexDirection: 'row',
    justifyContent: 'space-between', alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)', paddingHorizontal: 14, paddingVertical: 10 },
  recBadge:     { flexDirection: 'row', alignItems: 'center', backgroundColor: '#D32F2F',
    borderRadius: 8, paddingHorizontal: 8, paddingVertical: 3, marginLeft: 8 },

  videoTile:    { height: (height * 0.4) / 2, borderRadius: 12, overflow: 'hidden', marginBottom: 4,
    backgroundColor: '#1a237e' },
  videoTileInner: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 16 },
  tileLabel:    { color: '#fff', fontSize: 12, fontWeight: '700',
    backgroundColor: 'rgba(0,0,0,0.45)', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3,
    alignSelf: 'flex-start' },
  tileBottomBar:{ position: 'absolute', bottom: 6, left: 6, right: 6, flexDirection: 'row',
    alignItems: 'center', justifyContent: 'space-between' },
  mutedBanner:  { position: 'absolute', bottom: 8, right: 8, backgroundColor: '#D32F2F',
    borderRadius: 8, padding: 4 },
  flipCamBtn:   { position: 'absolute', top: 6, right: 6, backgroundColor: 'rgba(0,0,0,0.5)',
    width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  flipCamBtnLarge: { position: 'absolute', top: 12, right: 12, backgroundColor: 'rgba(0,0,0,0.55)',
    width: 38, height: 38, borderRadius: 19, alignItems: 'center', justifyContent: 'center' },

  // PiP
  pip:          { position: 'absolute', width: 110, height: 150, borderRadius: 12, overflow: 'hidden',
    backgroundColor: '#1a237e', borderWidth: 2, borderColor: 'rgba(255,255,255,0.4)',
    shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.4, shadowRadius: 8, elevation: 10,
    zIndex: 50 },

  // Mic activity panel
  micActivityPanel: { position: 'absolute', bottom: 16, left: 12, flexDirection: 'row',
    alignItems: 'center', gap: 6, backgroundColor: 'rgba(0,0,0,0.45)',
    paddingHorizontal: 10, paddingVertical: 6, borderRadius: 14 },

  // Bottom panel
  bottomPanel:  { backgroundColor: '#1a1a2e' },
  callTabBar:   { flexDirection: 'row', backgroundColor: 'rgba(0,0,0,0.4)',
    borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.1)' },
  callTab:      { flex: 1, alignItems: 'center', paddingVertical: 8, gap: 3 },
  callTabActive:{ backgroundColor: '#fff' },
  callTabLabel: { fontSize: 9, color: 'rgba(255,255,255,0.6)', fontWeight: '600', paddingHorizontal: 2 },

  badge:        { position: 'absolute', top: -4, right: -4, minWidth: 14, height: 14,
    paddingHorizontal: 3, borderRadius: 7,
    backgroundColor: '#D32F2F', alignItems: 'center', justifyContent: 'center' },

  // Participants tab
  paxCard:      { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 10,
    borderBottomWidth: 1, borderBottomColor: '#F5F7FA' },
  paxSection:   { fontSize: 11, fontWeight: '800', color: '#90A4AE', textTransform: 'uppercase',
    letterSpacing: 0.8, paddingHorizontal: 16, paddingVertical: 8, backgroundColor: '#FAFAFA' },
  paxName:      { fontSize: 14, fontWeight: '700', color: '#263238' },
  paxRole:      { fontSize: 12, color: '#78909C', marginTop: 1 },
  paxIcon:      { width: 30, height: 30, borderRadius: 15, backgroundColor: '#F5F5F5',
    alignItems: 'center', justifyContent: 'center' },
  youBadge:     { backgroundColor: '#E3F2FD', borderRadius: 8, paddingHorizontal: 6, paddingVertical: 2 },
  inviteBtn:    { flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    margin: 16, borderRadius: 12, borderWidth: 1.5, borderColor: '#0e4e96',
    borderStyle: 'dashed', paddingVertical: 14 },
  handQueueBar: { marginHorizontal: 12, marginTop: 10, padding: 10, backgroundColor: '#FFF3E0',
    borderRadius: 10, borderLeftWidth: 3, borderLeftColor: '#F57C00' },

  // Chat
  bubble:       { maxWidth: width * 0.72, borderRadius: 16, paddingHorizontal: 14, paddingVertical: 10 },
  bubbleMe:     { backgroundColor: '#0e4e96', borderBottomRightRadius: 4 },
  bubbleThem:   { backgroundColor: '#F5F7FA', borderBottomLeftRadius: 4 },
  chatInputRow: { flexDirection: 'row', alignItems: 'flex-end', paddingHorizontal: 14, paddingVertical: 10,
    borderTopWidth: 1, borderTopColor: '#ECEFF1', backgroundColor: '#fff', gap: 8 },
  chatInput:    { flex: 1, backgroundColor: '#F5F7FA', borderRadius: 20, paddingHorizontal: 16,
    paddingVertical: 10, fontSize: 14, color: '#263238', maxHeight: 100 },
  emojiBtn:     { width: 38, height: 38, alignItems: 'center', justifyContent: 'center' },
  sendBtn:      { width: 38, height: 38, borderRadius: 19, backgroundColor: '#0e4e96',
    alignItems: 'center', justifyContent: 'center' },
  reactionPicker: { flexDirection: 'row', flexWrap: 'wrap', backgroundColor: '#fff',
    paddingHorizontal: 10, paddingVertical: 8, borderTopWidth: 1, borderTopColor: '#ECEFF1' },
  reactionPickerFloating: { position: 'absolute', bottom: 80, left: 16, right: 16,
    backgroundColor: 'rgba(255,255,255,0.97)', borderRadius: 16, padding: 8, flexDirection: 'row',
    flexWrap: 'wrap', justifyContent: 'space-around',
    shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.2, shadowRadius: 10, elevation: 10 },

  // Info
  infoCard:     { backgroundColor: '#fff', borderRadius: 16, padding: 16, marginBottom: 12,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 5, elevation: 2 },
  infoTitle:    { fontSize: 16, fontWeight: '800', color: '#263238' },
  infoRow:      { flexDirection: 'row', alignItems: 'center', paddingVertical: 9,
    borderBottomWidth: 1, borderBottomColor: '#F5F7FA', gap: 10 },
  infoIcon:     { width: 28, height: 28, borderRadius: 14, backgroundColor: '#E3F2FD',
    alignItems: 'center', justifyContent: 'center' },
  infoLabel:    { width: 80, fontSize: 12, color: '#90A4AE', fontWeight: '600' },
  shareBtn:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    borderWidth: 1.5, borderColor: '#0e4e96', borderRadius: 14, paddingVertical: 14, marginTop: 6 },

  // Notes
  notesInput:   { backgroundColor: '#fff', borderRadius: 14, padding: 16, fontSize: 14,
    color: '#263238', lineHeight: 22, borderWidth: 1, borderColor: '#ECEFF1' },

  // End modal
  endModalBtn:  { flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    borderRadius: 14, paddingVertical: 15, borderWidth: 1.5 },

  // Invite modal
  inviteOption: { flexDirection: 'row', alignItems: 'center', gap: 14, paddingVertical: 14,
    borderBottomWidth: 1, borderBottomColor: '#F5F5F5' },
  inviteIcon:   { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center' },

  // Floating reaction
  floatingReaction: { position: 'absolute', alignItems: 'center', zIndex: 999 },

  // Captions
  captionsOverlay: { position: 'absolute', bottom: 80, left: 16, right: 16, gap: 6 },
  captionBubble:   { backgroundColor: 'rgba(0,0,0,0.7)', borderRadius: 10, padding: 10 },

  // Settings
  settingsLabel:   { fontSize: 11, fontWeight: '800', color: '#90A4AE', textTransform: 'uppercase',
    letterSpacing: 0.6, marginTop: 12, marginBottom: 6 },
  settingsRow:     { flexDirection: 'row', gap: 6, marginBottom: 6 },
  settingsChoice:  { flex: 1, paddingVertical: 10, borderRadius: 10, alignItems: 'center',
    backgroundColor: '#F5F7FA', borderWidth: 1, borderColor: '#E0E0E0' },
  settingsChoiceActive: { backgroundColor: '#0e4e96', borderColor: '#0e4e96' },
  settingsChoiceText:   { fontSize: 13, fontWeight: '700', color: '#546E7A' },
  settingsToggleRow:    { flexDirection: 'row', alignItems: 'center', paddingVertical: 14,
    borderBottomWidth: 1, borderBottomColor: '#F5F5F5' },
  toggle:          { width: 40, height: 22, borderRadius: 11, backgroundColor: '#CFD8DC',
    padding: 2, justifyContent: 'center' },
  toggleHandle:    { width: 18, height: 18, borderRadius: 9, backgroundColor: '#fff',
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.2, shadowRadius: 2, elevation: 2 },

  // More menu
  moreMenuItem:    { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, gap: 14 },
  moreMenuIcon:    { width: 38, height: 38, borderRadius: 19, alignItems: 'center', justifyContent: 'center' },

  // Polls
  createPollBtn:   { flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: '#0e4e96', borderRadius: 12, paddingVertical: 12, marginBottom: 16 },
  pollCard:        { backgroundColor: '#fff', borderRadius: 14, padding: 14, marginBottom: 12,
    borderWidth: 1, borderColor: '#ECEFF1',
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 4, elevation: 1 },
  pollOption:      { borderRadius: 10, marginBottom: 8, overflow: 'hidden', backgroundColor: '#F5F7FA',
    borderWidth: 1, borderColor: '#ECEFF1', position: 'relative' },
  pollBar:         { position: 'absolute', top: 0, bottom: 0, left: 0, backgroundColor: '#E3F2FD' },
  pollInput:       { backgroundColor: '#F5F7FA', borderRadius: 10, padding: 12, fontSize: 14,
    color: '#263238', borderWidth: 1, borderColor: '#ECEFF1', marginBottom: 10 },
});
